import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Checkbox, FormControlLabel, Alert, CircularProgress, Chip } from '@mui/material';
import { ItemFormData, ItemEntry, ProductConfiguration } from '../types';
import { Product } from '../../order';
import { PANEL_COUNT_OPTIONS } from '../constants';
import { useItemTypes, getItemTypeLabel } from '../../../../config/itemTypes';
import temporaryNumberingService from '../../../../services/temporaryNumberingService';
import { orderService } from '../../orderService';
import { useMasterDropdown } from '../../../../hooks/useMasterDropdown';
import MasterDropdown from '../../../../components/common/MasterDropdown';
import { masterDropdownService, MasterOption } from '../../../../services/masterDropdownService';
import { useDispatch } from 'react-redux';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { enhancedApiClient, isSuccessResponse } from '../../../../services/enhancedApiClient';
import Select from '@mui/material/Select';

const COLOR_OPTION = [
  { value: "jaipur_pink", label: "Jaipur Pink" },
  { value: "ral_8017", label: "RAL 8017" },
  { value: "ajetic_grey", label: "Ajetic Grey" },
  { value: "dark_grey", label: "Dark Grey" },
  { value: "sand_grey_8462", label: "Sand Grey 8462" },
  { value: "noir_grey", label: "Noir Grey" },
  { value: "dark_wood", label: "Dark Wood" },
  { value: "black_wood", label: "Black Wood" },
  { value: "light_wooden", label: "Light Wooden" },
  { value: "choc_brown", label: "Choc Brown" },
  { value: "black_str", label: "Black Str." },
  { value: "da_grey", label: "DA Grey" },
  { value: "anode_brown", label: "Anode Brown" },
  { value: "black_matt_9005", label: "Black Matt 9005" },
  { value: "pure_white_plain", label: "Pure White Plain" },
  { value: "pure_white_structure", label: "Pure White Structure" },
  { value: "munshil_grey", label: "Munshil Gery" },
  { value: "biscuit_beige", label: "Biscuit Biege" },
  { value: "ral_9010_mg", label: "RAL 9010 MG" },
  { value: "mat_grey", label: "Mat Grey" },
  { value: "sahara_grey", label: "Sahara Grey" },
  { value: "infinity_white", label: "Infinity White" },
];


interface ItemConfigurationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  onComplete: (productData: Product) => void;
  isEditMode?: boolean;
  onUpdate?: (productData: Product) => void;
  isIncrementalMode?: boolean; // New prop for incremental order creation
  onIncrementalComplete?: (productData: Product) => void; // New callback for incremental mode
  // New props for auto order creation
  customerId?: string;
  siteId?: string;
  orderId?: string; // Add orderId for editing existing orders
  orderData?: {
    amount?: string;
    paymentStatus?: string;
    priority?: string;
    dueDate?: string;
    notes?: string;
  };
  onOrderCreated?: (order: any) => void;
  onOrderUpdated?: (order: any) => void; // New callback for when order is updated
  onProductAdded?: (product: any) => void; // New callback for when product is added to existing order
  isReadOnly?: boolean; // New prop: when true, all fields and buttons are disabled/readonly
}

const ItemConfigurationPanel: React.FC<ItemConfigurationPanelProps> = ({
  isOpen,
  onClose,
  product,
  onComplete,
  isEditMode = false,
  onUpdate,
  isIncrementalMode = false,
  onIncrementalComplete,
  customerId,
  siteId,
  orderId,
  orderData,
  onOrderCreated,
  onOrderUpdated,
  onProductAdded,
  isReadOnly = false
}) => {

  // Fetch location options from master
  const { options: locationOptions, loading: locationLoading } = useMasterDropdown({
    masterType: 'locations',
    enabled: true,
  });

  // Fetch color options from master
  const { options: colorOptions, loading: colorLoading } = useMasterDropdown({
    masterType: 'colors',
    enabled: true,
  });

  // Fetch item types dynamically from item_masters table
  const { itemTypes: itemTypeOptions } = useItemTypes();
  
  // Helper function to get item type label by key (e.g., "frame" -> "Frame")
  const getItemTypeLabelByKey = (key: string): string => {
    // Explicit fallback mappings for ICM count keys
    const fallbackMap: Record<string, string> = {
      'frame': 'Frame',
      'panel': 'Panel',
      'panelwindow': 'Panel Window',
      'net': 'Net Fixing',
      'netfixing': 'Net Fixing'
    };
    
    const itemType = itemTypeOptions.find(it => {
      const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
      const normalizedValue = it.value.toLowerCase().replace(/\s+/g, '');
      const normalizedLabel = it.label.toLowerCase().replace(/\s+/g, '');
      return normalizedValue === normalizedKey || normalizedLabel === normalizedKey;
    });
    
    // If found in itemTypeOptions, use it; otherwise check fallback map; otherwise return key
    if (itemType?.label) {
      return itemType.label;
    }
    
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
    return fallbackMap[normalizedKey] || key;
  };

  // Helper function to determine if product type should have frame
  const shouldHaveFrame = (productType: string): boolean => {
    if (!productType) return false;
    const normalizedType = productType.toLowerCase().trim();
    // Product types that should have frames: door, window, ventilator
    return ['door', 'window', 'ventilator'].includes(normalizedType);
  };

  const initialProductType = product.type || '';
  // Frame is always selected by default
  const initialFrameChecked = true;

  const [formData, setFormData] = useState<ItemFormData>({
    label: product.label || '',
    location: product.location || '',
    productType: initialProductType,
    color: product.color || '',
    frameChecked: initialFrameChecked, // Frame always selected by default
    frameCount: 1, // Frame count always 1 by default
    panelCount: 0,
    panelWindowCount: 0,
    netCount: 0,
    panelLaserCutting: false // Default to false
  });

  useEffect(()=>{
    console.log("formdata--->",formData);
  },[formData])

  // Auto-sync state
  const [autoSyncDimensions, setAutoSyncDimensions] = useState(true);
  const dispatch = useDispatch();
  const [frameEntries, setFrameEntries] = useState<ItemEntry[]>([]);
  const [panelEntries, setPanelEntries] = useState<ItemEntry[]>([]);
  const [panelWindowEntries, setPanelWindowEntries] = useState<ItemEntry[]>([]);
  const [netEntries, setNetEntries] = useState<ItemEntry[]>([]);

  // Helper function to extract numeric value from item number for sorting
  // Examples: "F001" -> 1, "F002" -> 2, "P100" -> 100, "PW050" -> 50
  const getItemNumberValue = (itemNumber: string): number => {
    if (!itemNumber) return 0;
    // Extract numeric part from item number (e.g., "F001" -> "001" -> 1)
    const match = itemNumber.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  // Helper function to sort items by their number in ascending order
  const sortItemsByNumber = <T extends { number?: string; itemNumber?: string }>(items: T[]): T[] => {
    return [...items].sort((a, b) => {
      const numA = getItemNumberValue(a.number || a.itemNumber || '');
      const numB = getItemNumberValue(b.number || b.itemNumber || '');
      return numA - numB;
    });
  };

  // Helper function to sort entries by their number in ascending order
  const sortEntriesByNumber = (entries: ItemEntry[]): ItemEntry[] => {
    return [...entries].sort((a, b) => {
      const numA = getItemNumberValue(a.number || '');
      const numB = getItemNumberValue(b.number || '');
      return numA - numB;
    });
  };
  const [memberEntries, setMemberEntries] = useState<ItemEntry[]>([]);
  const [barEntries, setBarEntries] = useState<ItemEntry[]>([]);
  const [glassEntries, setGlassEntries] = useState<ItemEntry[]>([]);
  const [dynamicItemType, setDynamicItemType] = useState([]);
  
  // Subtype definitions for each item type
  const [availableSubtypes, setAvailableSubtypes] = useState<Record<string, Array<{
    subtypeName: string;
    selectionType: 'single' | 'multi';
    isRequired: boolean;
    displayOrder: number;
    options: Array<{
      value: string;
      label: string;
      optionOrder: number;
    }>;
  }>>>({});
  
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // //ItemDropDown call here
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await(masterDropdownService.getItemsDropdown());
        console.log("getItemsDropdown --->", response);
      } catch (error) {
        console.log("getItemsDropdown error --->", error);
      }
    };

    fetchData();
  }, []);

  // State for available item types (dynamic from master)
  const [availableItemTypes, setAvailableItemTypes] = useState<string[]>([]);
  // Ref to track if we've already fetched subtypes for this combination
  const fetchedSubtypesRef = useRef<string>('');
  // Ref to track if product type just changed (to prevent preserving old entries/subtypes)
  const productTypeChangedRef = useRef<boolean>(false);

  // Helper function to convert backend format (arrays) to UI format (string for single-select, array for multi-select)
  // Backend format: { "SubtypeName": ["value"] } or { "SubtypeName": ["value1", "value2"] }
  // UI format: { "SubtypeName": "value" } for single-select, { "SubtypeName": ["value1", "value2"] } for multi-select
  const convertSubtypesFromBackend = (subtypes: any): Record<string, any> => {
    console.log('🔄 convertSubtypesFromBackend called with:', subtypes, 'Type:', typeof subtypes, 'IsArray:', Array.isArray(subtypes));
    
    // Handle null, undefined, or empty values
    if (!subtypes) {
      console.log('⚠️ convertSubtypesFromBackend: Null/undefined input, returning empty object');
      return {};
    }
    
    // Handle string - might be JSON string that needs parsing
    if (typeof subtypes === 'string') {
      try {
        const parsed = JSON.parse(subtypes);
        console.log('🔄 convertSubtypesFromBackend: Parsed JSON string:', parsed);
        subtypes = parsed;
      } catch (e) {
        console.log('⚠️ convertSubtypesFromBackend: String is not valid JSON, returning empty object');
        return {};
      }
    }
    
    // Handle arrays - should not happen but handle gracefully
    if (Array.isArray(subtypes)) {
      console.log('⚠️ convertSubtypesFromBackend: Array input, returning empty object');
      return {};
    }
    
    // Must be an object at this point
    if (typeof subtypes !== 'object') {
      console.log('⚠️ convertSubtypesFromBackend: Invalid input type, returning empty object');
      return {};
    }
    
    const converted: Record<string, any> = {};
    
    Object.keys(subtypes).forEach(key => {
      if (!key || key === 'undefined' || typeof key !== 'string') {
        console.log('⚠️ convertSubtypesFromBackend: Skipping invalid key:', key);
        return;
      }
      
      const value = subtypes[key];
      console.log('🔄 convertSubtypesFromBackend: Processing key:', key, 'value:', value, 'valueType:', typeof value, 'isArray:', Array.isArray(value));
      
      // Backend stores as arrays, convert to UI format
      if (Array.isArray(value) && value.length > 0) {
        // If array has more than 1 element, it's definitely multi-select
        // If array has 1 element, assume single-select (convert to string)
        // The UI will handle it correctly based on the subtype definition
        if (value.length === 1) {
          // Single-select: convert array to string (take first element)
          converted[key] = value[0];
          console.log('✅ convertSubtypesFromBackend: Converted single-select array to string:', key, '->', value[0]);
        } else {
          // Multi-select: keep as array
          converted[key] = value;
          console.log('✅ convertSubtypesFromBackend: Kept multi-select as array:', key, '->', value);
        }
      } else if (typeof value === 'string' && value !== '') {
        // Already in UI format (string), keep as is
        converted[key] = value;
        console.log('✅ convertSubtypesFromBackend: Kept string value as is:', key, '->', value);
      } else if (value !== null && value !== undefined) {
        // Try to convert other types to string
        converted[key] = String(value);
        console.log('✅ convertSubtypesFromBackend: Converted value to string:', key, '->', String(value));
      } else {
        console.log('⚠️ convertSubtypesFromBackend: Skipping null/undefined value for key:', key);
      }
    });
    
    console.log('✅ convertSubtypesFromBackend: Final converted object:', converted);
    return converted;
  };

  // Helper function to clean subtype object - remove undefined keys and normalize to backend format
  // Backend expects: { "SubtypeName": ["value"] } for single-select, { "SubtypeName": ["value1", "value2"] } for multi-select
  const cleanSubtypeObject = (subtypes: any): Record<string, string[]> | null => {
    if (!subtypes || typeof subtypes !== 'object' || Array.isArray(subtypes)) {
      return null;
    }
    
    const cleaned: Record<string, string[]> = {};
    let hasValidKeys = false;
    
    Object.keys(subtypes).forEach(key => {
      // Skip undefined keys (the string 'undefined')
      // Allow valid string keys
      if (!key || key === 'undefined' || typeof key !== 'string') {
        return;
      }
      
      const value = subtypes[key];
      
      // Normalize value to array format (backend expects arrays even for single-select)
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Already an array - filter out empty strings and nulls
          const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
          if (filteredArray.length > 0) {
            cleaned[key] = filteredArray;
            hasValidKeys = true;
          }
        } else if (typeof value === 'string' && value !== '') {
          // Single-select: convert string to array
          cleaned[key] = [value];
          hasValidKeys = true;
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          // Convert number/boolean to string array
          cleaned[key] = [String(value)];
          hasValidKeys = true;
        }
      } else {
        console.log('🚫 Filtering out invalid subtype value:', { key, value, reason: value === undefined ? 'undefined' : 'null' });
      }
    });
    
    console.log('🧹 cleanSubtypeObject:', { input: subtypes, output: hasValidKeys ? cleaned : null });
    
    return hasValidKeys ? cleaned : null;
  };

  // Fetch available item types for the current product type (fully dynamic from master)
  useEffect(() => {
    const fetchItemTypes = async () => {
      const productType = formData.productType || product.type || '';
      
      if (!productType) {
        setAvailableItemTypes([]);
        return;
      }

      try {
        const response = await enhancedApiClient.get(
          `/item-subtype-definitions/item-types?productType=${encodeURIComponent(productType)}`
        );
        
        if (isSuccessResponse(response) && response.data) {
          // API returns { success: true, data: [...] }
          const backendResponse = response.data as any;
          const itemTypes = backendResponse?.data || backendResponse || [];
          
          if (Array.isArray(itemTypes) && itemTypes.length > 0) {
            setAvailableItemTypes(itemTypes);
          } else {
            // Fallback to empty array if no item types found
            setAvailableItemTypes([]);
          }
        }
      } catch (error) {
        console.error('Error fetching item types:', error);
        // Fallback to empty array on error
        setAvailableItemTypes([]);
      }
    };

    if (formData.productType || product.type) {
      fetchItemTypes();
    }
  }, [formData.productType, product.type]);

  // Memoize the item types key to prevent unnecessary re-renders
  const itemTypesKey = useMemo(() => {
    return [...new Set([...availableItemTypes, 'Frame', 'Net Fixing'])].sort().join(',');
  }, [availableItemTypes]);

  // Fetch available subtypes for product type + item type combinations
  // Fully dynamic - fetches for item types from master, API returns only what's configured
  // Also always fetches for Frame and Net Fixing to ensure subtypes are available for frame and net items
  useEffect(() => {
    const fetchSubtypes = async () => {
      const productType = formData.productType || product.type || '';

      if (!productType) {
        setAvailableSubtypes({});
        return;
      }
      
      // Create a unique key for this fetch
      const fetchKey = `${productType}-${itemTypesKey}`;
      
      // Skip if we've already fetched for this combination
      if (fetchedSubtypesRef.current === fetchKey) {
        return;
      }
      
      // Mark as fetched
      fetchedSubtypesRef.current = fetchKey;
      
      const subtypesMap: Record<string, any[]> = {};
      
      // Always include Frame and Net Fixing in the list of item types to fetch subtypes for
      // This ensures subtypes are available for frame and net items even if they're not in availableItemTypes
      const itemTypesToFetch = [...new Set([
        ...availableItemTypes,
        'Frame',
        'Net Fixing'
      ])];
      
      // If no item types available yet, still try to fetch for Frame and Net Fixing
      if (itemTypesToFetch.length === 0) {
        setAvailableSubtypes({});
        return;
      }
      
      // Fetch subtypes for each item type in parallel
      const fetchPromises = itemTypesToFetch.map(async (itemType) => {
        try {
          const response = await enhancedApiClient.get(
            `/item-subtype-definitions/available?productType=${encodeURIComponent(productType)}&itemType=${encodeURIComponent(itemType)}`
          );
          
          if (isSuccessResponse(response) && response.data) {
            // API returns { success: true, data: { hasSubtypes: boolean, subtypes: [...] } }
            // enhancedApiClient wraps it, so response.data is the backend response
            const backendResponse = response.data as any;
            const config = backendResponse?.data || backendResponse;
            const subtypes = config?.subtypes || [];
            
            console.log('📥 Fetched subtypes for:', { itemType, productType, response: response.data, config, subtypes, subtypesCount: subtypes.length });
            
            // Normalize subtypes: API returns "name" but we use "subtypeName" in the UI
            const normalizedSubtypes = subtypes.map((subtype: any) => ({
              ...subtype,
              subtypeName: subtype.subtypeName || subtype.name || subtype.subtype_name, // Support multiple field names
              name: subtype.name || subtype.subtypeName || subtype.subtype_name, // Keep name for backward compatibility
              uiComponentType: subtype.uiComponentType || subtype.ui_component_type || 'dropdown' // UI component type from master config
            }));
            
            // Only add to map if subtypes exist
            if (Array.isArray(normalizedSubtypes) && normalizedSubtypes.length > 0) {
              subtypesMap[itemType] = normalizedSubtypes;
              console.log('✅ Added subtypes to map for:', itemType, normalizedSubtypes);
            } else {
              console.log('⚠️ No subtypes found for:', itemType);
            }
          }
        } catch (error) {
          console.error(`Error fetching subtypes for ${itemType}:`, error);
          // Continue with other item types even if one fails
        }
      });
      
      await Promise.all(fetchPromises);
      console.log('📊 Final subtypesMap:', subtypesMap, 'Keys:', Object.keys(subtypesMap));
      setAvailableSubtypes(subtypesMap);
    };

    if (formData.productType || product.type) {
      fetchSubtypes();
    }
  }, [formData.productType, product.type, itemTypesKey]);


  // Debug: Monitor entry changes
  useEffect(() => {
    console.log('🔄 Entries updated:', {
      frame: frameEntries.length,
      panel: panelEntries.length,
      panelWindow: panelWindowEntries.length,
      net: netEntries.length
    });
  }, [frameEntries, panelEntries, panelWindowEntries, netEntries]);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({});

  // Auto-select frame - frame is always enabled by default
  useEffect(() => {
    // Only auto-select in create mode (not in edit mode with loaded data)
    if (!isEditMode || !hasLoadedExistingData) {
      setFormData(prev => {
        // Frame is always enabled - if not checked, enable it
        if (!prev.frameChecked) {
          return {
            ...prev,
            frameChecked: true,
            frameCount: prev.frameCount || 1
          };
        }
        return prev;
      });
    }
  }, [formData.productType, isEditMode, hasLoadedExistingData]);


  // Initialize form data when product changes (for edit mode)
  useEffect(() => {
    // Reset flags when panel opens with new product (only in create mode)
    if (!isEditMode) {
      setHasLoadedExistingData(false);
    }
    
    if (isEditMode && product) {
      // Load existing product configuration
      const existingConfig = product.metadata?.configuration;
      if (existingConfig) {
        const productType = product.type || '';
        // Frame is always selected by default
        setFormData({
          label: product.label || '',
          location: product.location || '',
          productType: productType,
          color: product.color || '',
          frameChecked: existingConfig.frameChecked !== undefined ? existingConfig.frameChecked : true, // Frame always selected by default
          frameCount: existingConfig.frameCount || 1, // Frame count always 1 by default
          panelCount: existingConfig.panelCount || 0,
          panelWindowCount: existingConfig.panelWindowCount || 0,
          netCount: existingConfig.netCount || 0,
          panelLaserCutting: existingConfig.panelLaserCutting !== undefined && existingConfig.panelLaserCutting !== null 
            ? Boolean(existingConfig.panelLaserCutting) 
            : false
        });
        
        // Load existing entries
        // Ensure subtypes are properly converted if they're in backend format
        // Sort entries by item number in ascending order
        const frameEntriesWithSubtypes = sortEntriesByNumber((existingConfig.frameEntries || []).map(entry => ({
          ...entry,
          subtypes: entry.subtypes ? convertSubtypesFromBackend(entry.subtypes) : (entry.subtypes || {})
        })));
        // Helper to convert backend value to boolean (handles 0/1, true/false, null/undefined)
        const toBoolean = (value: any, defaultValue: boolean): boolean => {
          if (value === null || value === undefined) return defaultValue;
          if (typeof value === 'boolean') return value;
          if (typeof value === 'number') return value !== 0;
          if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
          return defaultValue;
        };
        
        const panelEntriesWithSubtypes = sortEntriesByNumber((existingConfig.panelEntries || []).map(entry => {
          // If entry has originalItem, prefer values from there (most up-to-date)
          const sourceItem = entry.originalItem || entry;
          return {
            ...entry,
            // Ensure member/bar/glass values are properly set from originalItem or entry itself
            // API returns camelCase, so check both camelCase and snake_case for backward compatibility
            member: toBoolean(sourceItem.memberRequired ?? sourceItem.member_required ?? entry.member, true),
            bar: toBoolean(sourceItem.barRequired ?? sourceItem.bar_required ?? entry.bar, false),
            glass: toBoolean(sourceItem.glassRequired ?? sourceItem.glass_required ?? entry.glass, true),
            subtypes: entry.subtypes ? convertSubtypesFromBackend(entry.subtypes) : (entry.subtypes || {})
          };
        }));
        const panelWindowEntriesWithSubtypes = sortEntriesByNumber((existingConfig.panelWindowEntries || []).map(entry => ({
          ...entry,
          subtypes: entry.subtypes ? convertSubtypesFromBackend(entry.subtypes) : (entry.subtypes || {})
        })));
        const netEntriesWithSubtypes = sortEntriesByNumber((existingConfig.netEntries || []).map(entry => ({
          ...entry,
          subtypes: entry.subtypes ? convertSubtypesFromBackend(entry.subtypes) : (entry.subtypes || {})
        })));
        
        console.log('📦 Loading entries from metadata:', {
          frameEntries: frameEntriesWithSubtypes,
          panelEntries: panelEntriesWithSubtypes,
          panelWindowEntries: panelWindowEntriesWithSubtypes,
          netEntries: netEntriesWithSubtypes
        });
        
        setFrameEntries(frameEntriesWithSubtypes);
        setPanelEntries(panelEntriesWithSubtypes);
        setPanelWindowEntries(panelWindowEntriesWithSubtypes);
        setNetEntries(netEntriesWithSubtypes);
      } else if ((product.items && product.items.length > 0) || (product as any).frame || (product as any).panel || (product as any).panelWindow || (product as any).net) {
        // If no existing config but we have items, create entries from items
        // Check both legacy items array and new grouped structure
        
        // Group items by type if they come from a flat items array
        let frameItems = (product as any).frame || [];
        let panelItems = (product as any).panel || [];
        let panelWindowItems = (product as any).panelWindow || [];
        let netItems = (product as any).net || [];
        
        // If grouped arrays don't exist but we have items array, group them by type
        if (product.items && product.items.length > 0 && frameItems.length === 0 && panelItems.length === 0) {
          frameItems = product.items.filter((item: any) => item.type === 'Frame' || item.type === 'Door');
          panelItems = product.items.filter((item: any) => item.type === 'Panel');
          panelWindowItems = product.items.filter((item: any) => item.type === 'Panel Window');
          netItems = product.items.filter((item: any) => item.type === 'Net Fixing' || item.type === 'Net');
        }
        
        // Set the flag BEFORE setting form data to prevent useEffect from triggering
        setHasLoadedExistingData(true);
        
        const productType = product.type || '';
        // Frame is always selected by default
        // Helper to convert backend value to boolean (handles 0/1, true/false, null/undefined)
        const toBoolean = (value: any, defaultValue: boolean): boolean => {
          if (value === null || value === undefined) return defaultValue;
          if (typeof value === 'boolean') return value;
          if (typeof value === 'number') return value !== 0;
          if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
          return defaultValue;
        };
        
        // Check if any panel has isLaserCutting set to true (API returns camelCase)
        // Check both camelCase and snake_case for backward compatibility
        const panelLaserCutting = panelItems.length > 0 && panelItems.some((item: any) => 
          toBoolean(item.isLaserCutting ?? item.is_laser_cutting, false)
        );
        
        setFormData({
          label: product.label || '',
          location: product.location || '',
          productType: productType,
          color: product.color || '',
          frameChecked: frameItems.length > 0 || true, // Frame always selected by default
          frameCount: frameItems.length || 1, // Frame count always 1 by default
          panelCount: panelItems.length || 0,
          panelWindowCount: panelWindowItems.length || 0,
          netCount: netItems.length || 0,
          panelLaserCutting: panelLaserCutting
        });
        
        // Sort items by number before creating entries
        const sortedFrameItems = sortItemsByNumber(frameItems);
        const sortedPanelItems = sortItemsByNumber(panelItems);
        const sortedPanelWindowItems = sortItemsByNumber(panelWindowItems);
        const sortedNetItems = sortItemsByNumber(netItems);
        
        // Create entries from existing items
        setFrameEntries(sortedFrameItems.map((item, idx) => {
          // Use direct width/height properties from the item
          const width = (item as any)?.width || (item.specifications as any)?.width || '0';
          const height = (item as any)?.height || (item.specifications as any)?.height || '0';
          
          // Try multiple possible locations for subtype data
          const itemSubtype = (item as any)?.subtype || (item as any)?.subtypes || null;
          const convertedSubtypes = convertSubtypesFromBackend(itemSubtype);
          console.log(`📦 Loading Frame Item [${idx}]:`, {
            number: item.number || item.itemNumber,
            rawSubtype: itemSubtype,
            convertedSubtypes: convertedSubtypes,
            item: item,
            hasSubtype: !!itemSubtype,
            subtypeKeys: itemSubtype && typeof itemSubtype === 'object' ? Object.keys(itemSubtype) : []
          });

          return {
            number: item.number || item.itemNumber || '',
            width: String(parseInt(width) || 0),
            height: String(parseInt(height) || 0),
            member: false,
            glass: false,
            bar: false,
            subtypes: convertedSubtypes || {}, // Ensure subtypes is always an object
            originalItem: item // Include the original item object
          };
        }));
        
        setPanelEntries(sortedPanelItems.map((item, idx) => {
          // Use direct width/height properties from the item
          const width = (item as any)?.width || (item.specifications as any)?.width || '0';
          const height = (item as any)?.height || (item.specifications as any)?.height || '0';
          
          // Try multiple possible locations for subtype data
          const itemSubtype = (item as any)?.subtype || (item as any)?.subtypes || null;
          const convertedSubtypes = convertSubtypesFromBackend(itemSubtype);
          console.log(`📦 Loading Panel Item [${idx}]:`, {
            number: item.number || item.itemNumber,
            rawSubtype: itemSubtype,
            convertedSubtypes: convertedSubtypes,
            item: item,
            memberRequired: (item as any)?.memberRequired,
            barRequired: (item as any)?.barRequired,
            glassRequired: (item as any)?.glassRequired,
            isLaserCutting: (item as any)?.isLaserCutting,
            hasSubtype: !!itemSubtype,
            subtypeKeys: itemSubtype && typeof itemSubtype === 'object' ? Object.keys(itemSubtype) : []
          });

          return {
            number: item.number || item.itemNumber || '',
            width: String(parseInt(width) || 0),
            height: String(parseInt(height) || 0),
            // API returns camelCase, so check both camelCase and snake_case for backward compatibility
            member: toBoolean((item as any)?.memberRequired ?? (item as any)?.member_required, true),
            glass: toBoolean((item as any)?.glassRequired ?? (item as any)?.glass_required, true),
            bar: toBoolean((item as any)?.barRequired ?? (item as any)?.bar_required, false),
            subtypes: convertedSubtypes || {}, // Ensure subtypes is always an object
            originalItem: item // Include the original item object
          };
        }));
        
        setPanelWindowEntries(sortedPanelWindowItems.map((item, idx) => {
          // Use direct width/height properties from the item
          const width = (item as any)?.width || (item.specifications as any)?.width || '0';
          const height = (item as any)?.height || (item.specifications as any)?.height || '0';
          
          // Try multiple possible locations for subtype data
          const itemSubtype = (item as any)?.subtype || (item as any)?.subtypes || null;
          const convertedSubtypes = convertSubtypesFromBackend(itemSubtype);
          console.log(`📦 Loading PanelWindow Item [${idx}]:`, {
            number: item.number || item.itemNumber,
            rawSubtype: itemSubtype,
            convertedSubtypes: convertedSubtypes,
            item: item,
            hasSubtype: !!itemSubtype,
            subtypeKeys: itemSubtype && typeof itemSubtype === 'object' ? Object.keys(itemSubtype) : []
          });

          return {
            number: item.number || item.itemNumber || '',
            width: String(parseInt(width) || 0),
            height: String(parseInt(height) || 0),
            member: false,
            glass: false,
            bar: false,
            subtypes: convertedSubtypes || {}, // Ensure subtypes is always an object
            originalItem: item // Include the original item object
          };
        }));
        
        setNetEntries(sortedNetItems.map((item, idx) => {
          // Use direct width/height properties from the item
          const width = (item as any)?.width || (item.specifications as any)?.width || '0';
          const height = (item as any)?.height || (item.specifications as any)?.height || '0';
          
          // Try multiple possible locations for subtype data
          const itemSubtype = (item as any)?.subtype || (item as any)?.subtypes || null;
          const convertedSubtypes = convertSubtypesFromBackend(itemSubtype);
          console.log(`📦 Loading Net Item [${idx}]:`, {
            number: item.number || item.itemNumber,
            rawSubtype: itemSubtype,
            convertedSubtypes: convertedSubtypes,
            item: item,
            hasSubtype: !!itemSubtype,
            subtypeKeys: itemSubtype && typeof itemSubtype === 'object' ? Object.keys(itemSubtype) : []
          });

          return {
            number: item.number || item.itemNumber || '',
            width: String(parseInt(width) || 0),
            height: String(parseInt(height) || 0),
            member: false,
            glass: false,
            bar: false,
            subtypes: convertedSubtypes || {}, // Ensure subtypes is always an object
            originalItem: item // Include the original item object
          };
        }));
        
        // Set the flag to indicate we've loaded existing data
        setHasLoadedExistingData(true);
      }
    }
  }, [isEditMode, product]);

  // Generate entries based on counts for new items and handle count changes in edit mode
  useEffect(() => {
    console.log('🔄 useEffect triggered:', {
      isEditMode,
      hasLoadedExistingData,
      frameChecked: formData.frameChecked,
      frameCount: formData.frameCount,
      panelCount: formData.panelCount,
      panelWindowCount: formData.panelWindowCount,
      netCount: formData.netCount,
      frameEntriesLength: frameEntries.length,
      panelEntriesLength: panelEntries.length
    });
    
    // Skip if we're in edit mode and have loaded existing data (initial load)
    if (isEditMode && hasLoadedExistingData) {
      console.log('🔄 Skipping generation - edit mode with loaded data');
      return;
    }
    
    // Skip if we're in edit mode and already have entries with originalItem data
    const hasOriginalItemData = frameEntries.some(entry => entry.originalItem) || 
                               panelEntries.some(entry => entry.originalItem) ||
                               panelWindowEntries.some(entry => entry.originalItem) ||
                               netEntries.some(entry => entry.originalItem);
    
    if (isEditMode && hasOriginalItemData) {
      console.log('🔄 Skipping generation - edit mode with original item data');
      return;
    }
    
     // Only generate entries for new items or when counts change in create mode
     if (!isEditMode && !hasLoadedExistingData) {
      generateEntriesFromCounts();
    }
    
    // Additional safety: If we're in edit mode and have data, never call generateEntriesFromCounts
    if (isEditMode && hasOriginalItemData) {
      return;
    }
    // Final safety: If we're in edit mode, never call generateEntriesFromCounts
    if (isEditMode) {
      return;
    }

  }, [formData.frameChecked, formData.frameCount, formData.panelCount, formData.panelWindowCount, formData.netCount, isEditMode, hasLoadedExistingData]);

  const generateEntriesFromCounts = async (currentFormData?: ItemFormData) => {
    const formDataToUse = currentFormData || formData;
    
    console.log('🔄 generateEntriesFromCounts called with:', {
      frameChecked: formDataToUse.frameChecked,
      frameCount: formDataToUse.frameCount,
      panelCount: formDataToUse.panelCount,
      panelWindowCount: formDataToUse.panelWindowCount,
      netCount: formDataToUse.netCount
    });
    
    // Don't generate if we're in edit mode and have existing data
    if (isEditMode && hasLoadedExistingData && (frameEntries.length > 0 || panelEntries.length > 0)) {
      console.log('🔄 Skipping generation - edit mode with existing data');
      return;
    }
    
    // Don't generate if we're in edit mode and already have entries with originalItem data
    const hasOriginalItemData = frameEntries.some(entry => entry.originalItem) || 
                               panelEntries.some(entry => entry.originalItem) ||
                               panelWindowEntries.some(entry => entry.originalItem) ||
                               netEntries.some(entry => entry.originalItem);
    
    if (isEditMode && hasOriginalItemData) {
      console.log('🔄 Skipping generation - edit mode with original item data');
      return;
    }
    
    try {
      // Get latest ICM counts from database
      const response = await temporaryNumberingService.getLatestICMCounts();
      if (!response.success) {
        console.error('Failed to get latest ICM counts');
        return;
      }
      
      const latestCounts = response.data;
      
      // Generate frame entries - preserve existing entries when possible
      if (formDataToUse.frameChecked && formDataToUse.frameCount > 0) {
        console.log('🔄 Generating frame entries:', {
          frameChecked: formDataToUse.frameChecked,
          frameCount: formDataToUse.frameCount,
          startCount: (latestCounts[getItemTypeLabelByKey('frame')] || 0) + 1
        });
        const baseICMCount = latestCounts[getItemTypeLabelByKey('frame')] || 0;
        let newItemIndex = 0; // Track how many new items we've created
        const newFrameEntries = Array.from({ length: formDataToUse.frameCount }, (_, i) => {
          // If we have existing entries and this index exists, preserve it (especially if it has an ID)
          if (i < frameEntries.length) {
            const existingEntry = frameEntries[i];
            // If entry has originalItem with ID, it's an existing item - preserve its number
            if (existingEntry.originalItem?.id) {
              return existingEntry; // Keep existing item with its number
            }
            // If entry exists but no ID, preserve subtypes when count changes
            if (existingEntry.subtypes && Object.keys(existingEntry.subtypes).length > 0) {
              return {
                ...existingEntry,
                subtypes: { ...existingEntry.subtypes }
              };
            }
            return existingEntry;
          }
          // Otherwise create new entry with ICM + 1, ICM + 2, etc.
          // Account for existing entries that were preserved
          const nextCount = baseICMCount + frameEntries.length + newItemIndex + 1;
          const fullName = `F${String(nextCount).padStart(3, '0')}`; // F101 format
          newItemIndex++;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: {}
          };
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && frameEntries.length > 0 && frameEntries[0].width && frameEntries[0].height) {
            newEntry.width = frameEntries[0].width ;
            newEntry.height = frameEntries[0].height ;
          }
          
          return newEntry;
        });
        console.log('🔄 Setting frame entries:', newFrameEntries);
        setFrameEntries(newFrameEntries);
      } else {
        console.log('🔄 Clearing frame entries - frameChecked:', formDataToUse.frameChecked, 'frameCount:', formDataToUse.frameCount);
        setFrameEntries([]);
      }

      // Generate panel entries - preserve existing entries when possible
      if (formDataToUse.panelCount > 0) {
        const baseICMCount = latestCounts[getItemTypeLabelByKey('panel')] || 0;
        let newItemIndex = 0; // Track how many new items we've created
        const newPanelEntries = Array.from({ length: formDataToUse.panelCount }, (_, i) => {
          // If we have existing entries and this index exists, preserve it (especially if it has an ID)
          if (i < panelEntries.length) {
            const existingEntry = panelEntries[i];
            // If entry has originalItem with ID, it's an existing item - preserve its number
            if (existingEntry.originalItem?.id) {
              return existingEntry; // Keep existing item with its number
            }
            // If entry exists but no ID, preserve subtypes when count changes
            if (existingEntry.subtypes && Object.keys(existingEntry.subtypes).length > 0) {
              return {
                ...existingEntry,
                subtypes: { ...existingEntry.subtypes }
              };
            }
            return existingEntry;
          }
          // Otherwise create new entry with ICM + 1, ICM + 2, etc.
          // Account for existing entries that were preserved
          const nextCount = baseICMCount + panelEntries.length + newItemIndex + 1;
          const fullName = `P${String(nextCount).padStart(3, '0')}`; // P101 format
          newItemIndex++;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: true, // Default to true
            glass: true, // Default to true
            bar: true, // Default to true
            subtypes: {}
          };

          // If auto-sync is enabled and we have a first panel, copy its settings
          if (autoSyncDimensions && panelEntries.length > 0) {
            newEntry.member = panelEntries[0].member;
            newEntry.glass = panelEntries[0].glass;
            newEntry.bar = panelEntries[0].bar;
          }

          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && panelEntries.length > 0 && panelEntries[0].width && panelEntries[0].height) {
            newEntry.width = panelEntries[0].width;
            newEntry.height = panelEntries[0].height;
          }

          return newEntry;
        });
        setPanelEntries(newPanelEntries);
      } else {
        setPanelEntries([]);
      }

      // Generate panel window entries - preserve existing entries when possible
      if (formDataToUse.panelWindowCount > 0) {
        console.log("======>panelwindowcount:", formDataToUse.panelWindowCount);
        const baseICMCount = latestCounts[getItemTypeLabelByKey('panelWindow')] || 0;
        let newItemIndex = 0; // Track how many new items we've created
        const newPanelWindowEntries = Array.from({ length: formDataToUse.panelWindowCount }, (_, i) => {
          console.log("======>baseICMCount:", baseICMCount);
          console.log("======>panelWindowEntries:", panelWindowEntries);
          // If we have existing entries and this index exists, preserve it (especially if it has an ID)
          if (i < panelWindowEntries.length) {
            const existingEntry = panelWindowEntries[i];
            // If entry has originalItem with ID, it's an existing item - preserve its number
            if (existingEntry.originalItem?.id) {
              return existingEntry; // Keep existing item with its number
            }
            // If entry exists but no ID, preserve subtypes when count changes
            if (existingEntry.subtypes && Object.keys(existingEntry.subtypes).length > 0) {
              return {
                ...existingEntry,
                subtypes: { ...existingEntry.subtypes }
              };
            }
            return existingEntry;
          }
          // Otherwise create new entry with ICM + 1, ICM + 2, etc.
          // Account for existing entries that were preserved
          const nextCount = baseICMCount + panelWindowEntries.length + newItemIndex + 1;
          const fullName = `PW${String(nextCount).padStart(3, '0')}`; // PW101 format
          newItemIndex++;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: {}
          };
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && panelWindowEntries.length > 0 && panelWindowEntries[0].width && panelWindowEntries[0].height) {
            newEntry.width = panelWindowEntries[0].width;
            newEntry.height = panelWindowEntries[0].height;
          }
          
          return newEntry;
        });
        setPanelWindowEntries(newPanelWindowEntries);
      } else {
        setPanelWindowEntries([]);
      }

      // Generate net entries - preserve existing entries when possible
      if (formDataToUse.netCount > 0) {
        console.log("======>netcount:", formDataToUse.netCount);
              
        const netLabel = getItemTypeLabelByKey('net');
        const baseICMCount = latestCounts[netLabel] || 0;
        let newItemIndex = 0; // Track how many new items we've created
        console.log('🔄 Generating net entries:', {
          netCount: formDataToUse.netCount,
          netLabel,
          baseICMCount,
          latestCounts,
          availableKeys: Object.keys(latestCounts),
          currentNetEntries: netEntries.map(e => ({ number: e.number, subtypes: e.subtypes }))
        });
        const newNetEntries = Array.from({ length: formDataToUse.netCount }, (_, i) => {
          // If we have existing entries and this index exists, preserve it (especially if it has an ID)
          if (i < netEntries.length) {
            const existingEntry = netEntries[i];
            // If entry has originalItem with ID, it's an existing item - preserve its number
            if (existingEntry.originalItem?.id) {
              return existingEntry; // Keep existing item with its number
            }
            // If entry exists but no ID, preserve subtypes when count changes
            // This is important for keeping radio button selections in creation mode
            if (existingEntry.subtypes && Object.keys(existingEntry.subtypes).length > 0) {
              console.log('🔄 NET (generateEntriesFromCounts): Preserving subtypes for entry [' + i + ']:', existingEntry.subtypes);
              return {
                ...existingEntry,
                subtypes: { ...existingEntry.subtypes }
              };
            }
            // If entry exists but no subtypes, still return it as-is
            return existingEntry;
          }
          // Otherwise create new entry with ICM + 1, ICM + 2, etc.
          // Account for existing entries that were preserved
          const nextCount = baseICMCount + netEntries.length + newItemIndex + 1;
          const fullName = `N${String(nextCount).padStart(3, '0')}`; // N101 format
          newItemIndex++;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: {}
          };
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && netEntries.length > 0 && netEntries[0].width && netEntries[0].height) {
            newEntry.width = netEntries[0].width;
            newEntry.height = netEntries[0].height;
          }
          
          return newEntry;
        });
        console.log('🔄 NET (generateEntriesFromCounts): Final net entries:', newNetEntries.map(e => ({ number: e.number, subtypes: e.subtypes })));
        setNetEntries(newNetEntries);
      } else {
        setNetEntries([]);
      }
    } catch (error) {
      console.error('Error generating entries from counts:', error);
    }
  };



  const handleInputChange = (field: string, value: any) => {
    const updatedFormData = { ...formData, [field]: value };
    
    // If product type changes, reset all part values and entries
    if (field === 'productType') {
      // Mark that product type has changed to prevent preserving old entries/subtypes
      productTypeChangedRef.current = true;
      
      // Always reset panel, panel window, and net counts to 0
      updatedFormData.panelCount = 0;
      updatedFormData.panelWindowCount = 0;
      updatedFormData.netCount = 0;
      
      // Always clear panel, panel window, and net entries (with subtypes cleared)
      setPanelEntries([]);
      setPanelWindowEntries([]);
      setNetEntries([]);
      
      // Reset available subtypes and subtype fetch ref to force re-fetch for new product type
      setAvailableSubtypes({});
      fetchedSubtypesRef.current = '';
      
      // Frame is always enabled - preserve existing frame count or default to 1
      updatedFormData.frameChecked = true;
      // Preserve existing frame count if it exists and is > 0, otherwise default to 1
      updatedFormData.frameCount = (formData.frameCount > 0) ? formData.frameCount : 1;
      // Clear frame entries to ensure fresh start without old subtypes
      setFrameEntries([]);
    }
    
    setFormData(updatedFormData);
    
    // If this is a count field change, trigger entry regeneration
    if (['frameCount', 'panelCount', 'panelWindowCount', 'netCount', 'frameChecked'].includes(field)) {
      console.log(`🔄 Count change detected: ${field} = ${value}`);
      // Immediately trigger entry regeneration for count changes
      handleCountChange(updatedFormData);
    }
    
    // If product type changed, also trigger entry regeneration to recreate frame entries with preserved count
    if (field === 'productType') {
      console.log(`🔄 Product type changed, regenerating entries with frameCount: ${updatedFormData.frameCount}`);
      handleCountChange(updatedFormData);
    }
  };

  // New function specifically for handling count changes
  const handleCountChange = async (currentFormData: ItemFormData) => {
    try {
      console.log('🔄 handleCountChange called with:', currentFormData);
      // Get latest ICM counts from database
      const response = await temporaryNumberingService.getLatestICMCounts();
      if (!response.success) {
        console.error('Failed to get latest ICM counts');
        return;
      }
      
      const latestCounts = response.data;
      console.log('🔄 Latest ICM counts:', latestCounts);
      
      // PRESERVE SUBTYPES: Save current subtypes before regenerating entries (creation mode only)
      // In edit mode, subtypes should not change during count adjustments
      const preserveSubtypesFromExisting = (existingEntries: ItemEntry[], newCount: number): Record<number, Record<string, any>> => {
        const savedSubtypes: Record<number, Record<string, any>> = {};
        for (let i = 0; i < Math.min(existingEntries.length, newCount); i++) {
          if (existingEntries[i]?.subtypes && Object.keys(existingEntries[i].subtypes).length > 0) {
            savedSubtypes[i] = { ...existingEntries[i].subtypes };
          }
        }
        return savedSubtypes;
      };
      
      // Generate frame entries based on new count
      if (currentFormData.frameChecked && currentFormData.frameCount > 0) {
        const startCount = (latestCounts[getItemTypeLabelByKey('frame')] || 0) + 1;
        // In creation mode (not edit mode), preserve subtypes when count changes
        const savedFrameSubtypes = !isEditMode ? preserveSubtypesFromExisting(frameEntries, currentFormData.frameCount) : {};
        // If product type changed, treat existingCount as 0 since we're creating fresh entries
        const existingCount = productTypeChangedRef.current ? 0 : frameEntries.length;
        const newFrameEntries = Array.from({ length: currentFormData.frameCount }, (_, i) => {
          // If product type changed, don't preserve old entries - create fresh ones
          // If we have existing entries and this index exists, preserve it completely (including subtypes)
          if (!productTypeChangedRef.current && i < existingCount && frameEntries[i]) {
            // Deep copy to ensure subtypes object is preserved
            return {
              ...frameEntries[i],
              subtypes: frameEntries[i].subtypes ? { ...frameEntries[i].subtypes } : {}
            };
          }
          // Otherwise create new entry - offset by number of existing entries
          // New entries should continue from where existing entries left off
          const newItemOffset = i - existingCount;
          const fullName = `F${String(startCount + existingCount + newItemOffset).padStart(3, '0')}`;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: savedFrameSubtypes[i] || {} // Restore saved subtypes if available
          };
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && frameEntries.length > 0 && frameEntries[0].width && frameEntries[0].height) {
            newEntry.width = frameEntries[0].width;
            newEntry.height = frameEntries[0].height;
          }
          
          return newEntry;
        });
        console.log(`🔄 Setting ${newFrameEntries.length} frame entries`, newFrameEntries.map(e => ({ number: e.number, hasSubtypes: !!e.subtypes && Object.keys(e.subtypes).length > 0, subtypes: e.subtypes })));
        setFrameEntries(newFrameEntries);
      } else {
        console.log('🔄 Clearing frame entries');
        setFrameEntries([]);
      }

      // Generate panel entries based on new count
      if (currentFormData.panelCount > 0) {
        const startCount = (latestCounts[getItemTypeLabelByKey('panel')] || 0) + 1;
        // In creation mode (not edit mode), preserve subtypes when count changes
        const savedPanelSubtypes = !isEditMode ? preserveSubtypesFromExisting(panelEntries, currentFormData.panelCount) : {};
        // If product type changed, treat existingCount as 0 since we're creating fresh entries
        const existingCount = productTypeChangedRef.current ? 0 : panelEntries.length;
        const newPanelEntries = Array.from({ length: currentFormData.panelCount }, (_, i) => {
          // If product type changed, don't preserve old entries - create fresh ones
          // If we have existing entries and this index exists, preserve it completely (including subtypes)
          if (!productTypeChangedRef.current && i < existingCount && panelEntries[i]) {
            // Deep copy to ensure subtypes object is preserved
            return {
              ...panelEntries[i],
              subtypes: panelEntries[i].subtypes ? { ...panelEntries[i].subtypes } : {}
            };
          }
          // Otherwise create new entry - offset by number of existing entries
          // New entries should continue from where existing entries left off
          const newItemOffset = i - existingCount;
          const fullName = `P${String(startCount + existingCount + newItemOffset).padStart(3, '0')}`;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: true, // Default to true
            glass: true, // Default to true
            bar: true, // Default to true
            subtypes: savedPanelSubtypes[i] || {} // Restore saved subtypes if available
          };

          // If auto-sync is enabled and we have a first panel, copy its settings
          if (autoSyncDimensions && panelEntries.length > 0) {
            newEntry.member = panelEntries[0].member;
            newEntry.glass = panelEntries[0].glass;
            newEntry.bar = panelEntries[0].bar;
          }

          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && panelEntries.length > 0 && panelEntries[0].width && panelEntries[0].height) {
            newEntry.width = panelEntries[0].width;
            newEntry.height = panelEntries[0].height;
          }

          return newEntry;
        });
        setPanelEntries(newPanelEntries);
      } else {
        setPanelEntries([]);
      }

      // Generate panel window entries based on new count
      if (currentFormData.panelWindowCount > 0) {
        const startCount = (latestCounts[getItemTypeLabelByKey('panelWindow')] || 0) + 1;
        // In creation mode (not edit mode), preserve subtypes when count changes
        const savedPanelWindowSubtypes = !isEditMode ? preserveSubtypesFromExisting(panelWindowEntries, currentFormData.panelWindowCount) : {};
        // If product type changed, treat existingCount as 0 since we're creating fresh entries
        const existingCount = productTypeChangedRef.current ? 0 : panelWindowEntries.length;
        const newPanelWindowEntries = Array.from({ length: currentFormData.panelWindowCount }, (_, i) => {
          // If product type changed, don't preserve old entries - create fresh ones
          // If we have existing entries and this index exists, preserve it completely (including subtypes)
          if (!productTypeChangedRef.current && i < existingCount && panelWindowEntries[i]) {
            // Deep copy to ensure subtypes object is preserved
            return {
              ...panelWindowEntries[i],
              subtypes: panelWindowEntries[i].subtypes ? { ...panelWindowEntries[i].subtypes } : {}
            };
          }
          // Otherwise create new entry - offset by number of existing entries
          // New entries should continue from where existing entries left off
          const newItemOffset = i - existingCount;
          const fullName = `PW${String(startCount + existingCount + newItemOffset).padStart(3, '0')}`;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: savedPanelWindowSubtypes[i] || {} // Restore saved subtypes if available
          };
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && panelWindowEntries.length > 0 && panelWindowEntries[0].width && panelWindowEntries[0].height) {
            newEntry.width = panelWindowEntries[0].width;
            newEntry.height = panelWindowEntries[0].height;
          }
          
          return newEntry;
        });
        setPanelWindowEntries(newPanelWindowEntries);
      } else {
        setPanelWindowEntries([]);
      }

      // Generate net entries based on new count
      if (currentFormData.netCount > 0) {
        const netLabel = getItemTypeLabelByKey('net');
        const netCountFromICM = latestCounts[netLabel] || 0;
        const startCount = netCountFromICM + 1;
        // In creation mode (not edit mode), preserve subtypes when count changes
        const savedNetSubtypes = !isEditMode ? preserveSubtypesFromExisting(netEntries, currentFormData.netCount) : {};
        // If product type changed, treat existingCount as 0 since we're creating fresh entries
        const existingCount = productTypeChangedRef.current ? 0 : netEntries.length;
        console.log('🔄 Generating net entries (handleCountChange):', {
          netCount: currentFormData.netCount,
          netLabel,
          netCountFromICM,
          startCount,
          existingCount,
          isEditMode,
          currentNetEntries: netEntries.map(e => ({ number: e.number, subtypes: e.subtypes })),
          latestCounts,
          availableKeys: Object.keys(latestCounts),
          savedNetSubtypes
        });
        const newNetEntries = Array.from({ length: currentFormData.netCount }, (_, i) => {
          // If product type changed, don't preserve old entries - create fresh ones
          // If we have existing entries and this index exists, preserve it completely (including subtypes)
          if (!productTypeChangedRef.current && i < existingCount && netEntries[i]) {
            // Deep copy to ensure subtypes object is preserved
            const preservedEntry = {
              ...netEntries[i],
              subtypes: netEntries[i].subtypes ? { ...netEntries[i].subtypes } : {}
            };
            console.log('🔄 NET: Preserving existing entry [' + i + ']:', { number: preservedEntry.number, subtypes: preservedEntry.subtypes });
            return preservedEntry;
          }
          // Otherwise create new entry - offset by number of existing entries
          // New entries should continue from where existing entries left off
          const newItemOffset = i - existingCount;
          const fullName = `N${String(startCount + existingCount + newItemOffset).padStart(3, '0')}`;
          const newEntry = {
            number: fullName,
            width: '',
            height: '',
            member: false,
            glass: false,
            bar: false,
            subtypes: savedNetSubtypes[i] || {} // Restore saved subtypes if available
          };
          
          console.log('🔄 NET: Creating new entry [' + i + '] with restored subtypes:', { number: newEntry.number, subtypes: newEntry.subtypes, fromSaved: savedNetSubtypes[i] });
          
          // If auto-sync is enabled and we have a first item with dimensions, copy them
          if (autoSyncDimensions && netEntries.length > 0 && netEntries[0].width && netEntries[0].height) {
            newEntry.width = netEntries[0].width;
            newEntry.height = netEntries[0].height;
          }
          
          return newEntry;
        });
        console.log('🔄 NET: Final entries to be set:', newNetEntries.map(e => ({ number: e.number, subtypes: e.subtypes })));
        setNetEntries(newNetEntries);
      } else {
        setNetEntries([]);
      }
      
      // Reset the product type changed flag after entries are regenerated
      productTypeChangedRef.current = false;
    } catch (error) {
      console.error('Error handling count change:', error);
      // Reset the flag even on error
      productTypeChangedRef.current = false;
    }
  };

  const handleEntryChange = (type: string, index: number, field: string, value: any) => {
    const setters = {
      frame: setFrameEntries,
      panel: setPanelEntries,
      panelWindow: setPanelWindowEntries,
      net: setNetEntries,
      member: setMemberEntries,
      bar: setBarEntries,
      glass: setGlassEntries
    };
    
    const entries = {
      frame: frameEntries,
      panel: panelEntries,
      panelWindow: panelWindowEntries,
      net: netEntries,
      member: memberEntries,
      bar: barEntries,
      glass: glassEntries
    };

    // Handle subtype changes
    if (field.startsWith('subtype_')) {
      const subtypeName = field.replace('subtype_', '');
      
      // Validate subtypeName - must be a non-empty string
      if (!subtypeName || subtypeName.trim() === '') {
        console.warn('Invalid subtype name:', subtypeName, 'from field:', field);
        return;
      }
      
      const setter = setters[type as keyof typeof setters];
      const currentEntries = entries[type as keyof typeof entries];
      
      if (setter && currentEntries && currentEntries[index]) {
        const updatedEntries = [...currentEntries];
        const entry = { ...updatedEntries[index] };
        
        if (!entry.subtypes) {
          entry.subtypes = {};
        }
        
        // Clean up any undefined keys before setting new value
        const cleanedSubtypes: Record<string, any> = {};
        Object.keys(entry.subtypes || {}).forEach(key => {
          if (key !== 'undefined' && key !== undefined) {
            cleanedSubtypes[key] = entry.subtypes[key];
          }
        });
        entry.subtypes = cleanedSubtypes;
        
        // Set the new subtype value
        // Only set if value is not empty string (empty string means "no selection" for single-select)
        // For multi-select, empty array is valid
        if (value === '' || value === null) {
          // Remove the subtype key if value is empty/null
          delete entry.subtypes[subtypeName];
        } else {
          entry.subtypes[subtypeName] = value;
          // Clear validation error when subtype is selected
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${type}-${index}-subtype-${subtypeName}`];
            return newErrors;
          });
        }
        console.log('🔧 Setting subtype:', { subtypeName, value, valueType: typeof value, isArray: Array.isArray(value), entry: entry.subtypes, fullEntry: entry });
        updatedEntries[index] = entry;
        setter(updatedEntries);
      }
      return;
    }

    const setter = setters[type as keyof typeof setters];
    const entryList = entries[type as keyof typeof entries];
    
    const newEntries = [...entryList];
    
    // Validate width and height as integers
    if (field === 'width' || field === 'height') {
      // Only allow integers
      const intValue = parseInt(value);
      if (isNaN(intValue) || intValue <= 0) {
        // Mark as invalid but still allow typing
        setValidationErrors(prev => ({
          ...prev,
          [`${type}-${index}-${field}`]: true
        }));
      } else {
        // Clear validation error
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`${type}-${index}-${field}`];
          return newErrors;
        });
        value = intValue.toString();
      }
    }
    
    newEntries[index] = { ...newEntries[index], [field]: value };
    setter(newEntries);

    // Auto-sync dimensions and panel settings if enabled and this is the first item of any type
    if (autoSyncDimensions && index === 0) {
      // For panels, sync width, height, member, glass, and bar
      if (type === 'panel' && (field === 'width' || field === 'height' || field === 'member' || field === 'glass' || field === 'bar')) {
        // Apply the same settings to all other panels
        const allEntries = [...newEntries];
        for (let i = 1; i < allEntries.length; i++) {
          allEntries[i] = { ...allEntries[i], [field]: value };
        }
        setter(allEntries);
      }
      // For other types, sync only dimensions
      else if (field === 'width' || field === 'height') {
        // Apply the same dimensions to all other items of the same type
        const allEntries = [...newEntries];
        for (let i = 1; i < allEntries.length; i++) {
          allEntries[i] = { ...allEntries[i], [field]: value };
        }
        setter(allEntries);
      }
    }
  };

  // Validation function to check if all required fields are filled
  const validateForm = () => {
    const errors: {[key: string]: boolean} = {};
    let isValid = true;
    const productType = formData.productType || product.type || '';

    // Check all entries for width and height
    const allEntries = [
      ...frameEntries.map((entry, index) => ({ entry, type: 'frame', index, itemType: 'Frame' })),
      ...panelEntries.map((entry, index) => ({ entry, type: 'panel', index, itemType: 'Panel' })),
      ...panelWindowEntries.map((entry, index) => ({ entry, type: 'panelWindow', index, itemType: 'Panel Window' })),
      ...netEntries.map((entry, index) => ({ entry, type: 'net', index, itemType: 'Net Fixing' }))
    ];

    allEntries.forEach(({ entry, type, index, itemType }) => {
      const width = parseInt(entry.width);
      const height = parseInt(entry.height);
      
      if (isNaN(width) || width <= 0 || entry.width === '') {
        errors[`${type}-${index}-width`] = true;
        isValid = false;
      }
      
      if (isNaN(height) || height <= 0 || entry.height === '') {
        errors[`${type}-${index}-height`] = true;
        isValid = false;
      }

      // Validate subtypes - if subtypes are available, at least one must be selected
      // Only check if subtypes exist for this item type and it's a valid array with items
      if (productType && availableSubtypes[itemType] && Array.isArray(availableSubtypes[itemType]) && availableSubtypes[itemType].length > 0) {
        const subtypesForItem = availableSubtypes[itemType];
        // Filter out "None" subtypes for validation
        const validSubtypes = subtypesForItem.filter(subtypeDef => 
          subtypeDef && 
          subtypeDef.subtypeName && 
          subtypeDef.subtypeName.trim() !== '' && 
          subtypeDef.subtypeName !== 'None'
        );
        
        // Only require selection if there are actually valid subtypes available
        if (validSubtypes.length > 0) {
          let hasAtLeastOneSubtypeSelected = false;
          
          // Check if at least one subtype has a selection
          validSubtypes.forEach((subtypeDef) => {
            const currentValue = entry.subtypes?.[subtypeDef.subtypeName];
            
            if (subtypeDef.selectionType === 'multi') {
              // For multi-select, check if at least one option is selected
              if (Array.isArray(currentValue) && currentValue.length > 0) {
                hasAtLeastOneSubtypeSelected = true;
              }
            } else {
              // For single-select, check if a value is selected (not empty string or null)
              if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                hasAtLeastOneSubtypeSelected = true;
              }
            }
          });
          
          // If no subtype is selected, mark all subtypes as errors
          if (!hasAtLeastOneSubtypeSelected) {
            validSubtypes.forEach((subtypeDef) => {
              errors[`${type}-${index}-subtype-${subtypeDef.subtypeName}`] = true;
            });
            isValid = false;
          }
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    // Check if set type is selected
    if (formData.productType === 'door') {
      return false;
    }

    const productType = formData.productType || product.type || '';
    
    // Check if all entries have valid width and height
    const allEntries = [
      ...frameEntries.map((entry, index) => ({ entry, type: 'frame', index, itemType: 'Frame' })),
      ...panelEntries.map((entry, index) => ({ entry, type: 'panel', index, itemType: 'Panel' })),
      ...panelWindowEntries.map((entry, index) => ({ entry, type: 'panelWindow', index, itemType: 'Panel Window' })),
      ...netEntries.map((entry, index) => ({ entry, type: 'net', index, itemType: 'Net Fixing' }))
    ];

    return allEntries.every(({ entry, itemType }) => {
      const width = entry.width ? parseInt(entry.width) : 0;
      const height = entry.height ? parseInt(entry.height) : 0;
      
      // Check width and height
      if (isNaN(width) || width <= 0 || entry.width === '' || isNaN(height) || height <= 0 || entry.height === '') {
        return false;
      }
      
      // Check subtypes - if subtypes are available, at least one must be selected
      // Only check if subtypes exist for this item type and it's a valid array with items
      if (productType && availableSubtypes[itemType] && Array.isArray(availableSubtypes[itemType]) && availableSubtypes[itemType].length > 0) {
        const subtypesForItem = availableSubtypes[itemType];
        // Filter out "None" subtypes for validation
        const validSubtypes = subtypesForItem.filter(subtypeDef => 
          subtypeDef && 
          subtypeDef.subtypeName && 
          subtypeDef.subtypeName.trim() !== '' && 
          subtypeDef.subtypeName !== 'None'
        );
        
        // Only require selection if there are actually valid subtypes available
        if (validSubtypes.length > 0) {
          let hasAtLeastOneSubtypeSelected = false;
          
          // Check if at least one subtype has a selection
          validSubtypes.forEach((subtypeDef) => {
            const currentValue = entry.subtypes?.[subtypeDef.subtypeName];
            
            if (subtypeDef.selectionType === 'multi') {
              // For multi-select, check if at least one option is selected
              if (Array.isArray(currentValue) && currentValue.length > 0) {
                hasAtLeastOneSubtypeSelected = true;
              }
            } else {
              // For single-select, check if a value is selected (not empty string or null)
              if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                hasAtLeastOneSubtypeSelected = true;
              }
            }
          });
          
          // If no subtype is selected, form is invalid
          if (!hasAtLeastOneSubtypeSelected) {
            return false;
          }
        }
      }
      
      return true;
    });
  };

  // Helper function to transform product to itemTypes format for API
  const transformProductToItemTypes = (product: Product) => {
    // Debug: Log all entries before transformation
    console.log('🔄 transformProductToItemTypes called with entries:', {
      frameEntries: frameEntries.map(e => ({ number: e.number, subtypes: e.subtypes })),
      panelEntries: panelEntries.map(e => ({ number: e.number, subtypes: e.subtypes })),
      netEntries: netEntries.map(e => ({ number: e.number, subtypes: e.subtypes })),
      panelWindowEntries: panelWindowEntries.map(e => ({ number: e.number, subtypes: e.subtypes }))
    });
    
    const itemTypes: Record<string, any[]> = {
      frame: [],
      panel: [],
      panelWindow: [],
      net: [],
      openableType: [],
      door: []
    };

    // Transform frame entries
    frameEntries.forEach((entry, entryIndex) => {
      console.log(`🔍 Frame entry [${entryIndex}] (${entry.number}):`, {
        entry,
        hasSubtypes: !!entry.subtypes,
        subtypesKeys: entry.subtypes ? Object.keys(entry.subtypes) : [],
        subtypes: entry.subtypes,
        hasOriginalItem: !!entry.originalItem,
        originalItemId: entry.originalItem?.id
      });
      
      const cleanedSubtypes = cleanSubtypeObject(entry.subtypes);
      console.log(`🧹 Frame entry [${entryIndex}] cleaned subtypes:`, cleanedSubtypes);
      
      const itemData: any = {
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        number: entry.number || null,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        subtype: cleanedSubtypes // Clean and include subtypes in JSONB format
      };
      
      // Include item ID if available (from originalItem), or set alreadyExist flag
      if (entry.originalItem?.id) {
        itemData.id = entry.originalItem.id;
      } else if (entry.originalItem) {
        // If originalItem exists but no ID, mark as existing item
        itemData.alreadyExist = true;
      }
      
      console.log(`📦 Frame itemData [${entryIndex}]:`, itemData);
      itemTypes.frame.push(itemData);
    });

    // Transform panel entries
    panelEntries.forEach((entry, entryIndex) => {
      console.log(`🔍 Panel entry [${entryIndex}] (${entry.number}):`, {
        entry,
        hasSubtypes: !!entry.subtypes,
        subtypesKeys: entry.subtypes ? Object.keys(entry.subtypes) : [],
        subtypes: entry.subtypes,
        hasOriginalItem: !!entry.originalItem,
        originalItemId: entry.originalItem?.id
      });
      
      const cleanedSubtypes = cleanSubtypeObject(entry.subtypes);
      console.log(`🧹 Panel entry [${entryIndex}] cleaned subtypes:`, cleanedSubtypes);
      
      const itemData: any = {
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        number: entry.number || null,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        bar_required: entry.bar ?? false,
        member_required: entry.member ?? false,
        glass_required: entry.glass ?? false,
        is_laser_cutting: formData.panelLaserCutting ?? false, // Apply group-level setting to all panels
        assigned_to: null,
        subtype: cleanedSubtypes // Clean and include subtypes in JSONB format
      };
      
      // Include item ID if available (from originalItem), or set alreadyExist flag
      if (entry.originalItem?.id) {
        itemData.id = entry.originalItem.id;
      } else if (entry.originalItem) {
        // If originalItem exists but no ID, mark as existing item
        itemData.alreadyExist = true;
      }
      
      console.log(`📦 Panel itemData [${entryIndex}]:`, itemData);
      itemTypes.panel.push(itemData);
    });

    // Transform panel window entries
    panelWindowEntries.forEach(entry => {
      const itemData: any = {
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        number: entry.number || null,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        subtype: cleanSubtypeObject(entry.subtypes) // Clean and include subtypes in JSONB format
      };
      
      // Include item ID if available (from originalItem), or set alreadyExist flag
      if (entry.originalItem?.id) {
        itemData.id = entry.originalItem.id;
      } else if (entry.originalItem) {
        // If originalItem exists but no ID, mark as existing item
        itemData.alreadyExist = true;
      }
      
      itemTypes.panelWindow.push(itemData);
    });

    // Transform net entries
    netEntries.forEach((entry, entryIndex) => {
      console.log(`🔍 Net entry [${entryIndex}] (${entry.number}):`, {
        entry,
        hasSubtypes: !!entry.subtypes,
        subtypesKeys: entry.subtypes ? Object.keys(entry.subtypes) : [],
        subtypes: entry.subtypes,
        hasOriginalItem: !!entry.originalItem,
        originalItemId: entry.originalItem?.id
      });
      
      const cleanedSubtypes = cleanSubtypeObject(entry.subtypes);
      console.log(`🧹 Net entry [${entryIndex}] cleaned subtypes:`, cleanedSubtypes);
      
      const itemData: any = {
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        number: entry.number || null,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        subtype: cleanedSubtypes // Clean and include subtypes in JSONB format
      };
      
      // Include item ID if available (from originalItem), or set alreadyExist flag
      if (entry.originalItem?.id) {
        itemData.id = entry.originalItem.id;
      } else if (entry.originalItem) {
        // If originalItem exists but no ID, mark as existing item
        itemData.alreadyExist = true;
      }
      
      console.log(`📦 Net itemData [${entryIndex}]:`, itemData);
      itemTypes.net.push(itemData);
    });

    return itemTypes;
  };

  // Helper function to check for missing subtypes and generate warning message
  const checkMissingSubtypes = (): string | null => {
    const productType = formData.productType || product.type || '';
    if (!productType) return null;

    // Don't validate if subtypes haven't been loaded yet (availableSubtypes is empty or still loading)
    // This prevents false positives when entries are regenerated before subtypes are fetched
    if (!availableSubtypes || Object.keys(availableSubtypes).length === 0) {
      return null;
    }

    const allEntries = [
      ...frameEntries.map((entry, index) => ({ entry, type: 'frame', index, itemType: 'Frame', itemLabel: 'Frame' })),
      ...panelEntries.map((entry, index) => ({ entry, type: 'panel', index, itemType: 'Panel', itemLabel: 'Panel' })),
      ...panelWindowEntries.map((entry, index) => ({ entry, type: 'panelWindow', index, itemType: 'Panel Window', itemLabel: 'Panel Window' })),
      ...netEntries.map((entry, index) => ({ entry, type: 'net', index, itemType: 'Net Fixing', itemLabel: 'Net Fixing' }))
    ];

    const missingSubtypes: string[] = [];

    allEntries.forEach(({ entry, itemType, itemLabel }) => {
      // Only check if subtypes exist for this item type and it's a valid array with items
      if (availableSubtypes[itemType] && Array.isArray(availableSubtypes[itemType]) && availableSubtypes[itemType].length > 0) {
        const subtypesForItem = availableSubtypes[itemType];
        const validSubtypes = subtypesForItem.filter(subtypeDef => 
          subtypeDef && 
          subtypeDef.subtypeName && 
          subtypeDef.subtypeName.trim() !== '' && 
          subtypeDef.subtypeName !== 'None'
        );
        
        // Only require selection if there are actually valid subtypes available
        if (validSubtypes.length > 0) {
          let hasAtLeastOneSubtypeSelected = false;
          
          validSubtypes.forEach((subtypeDef) => {
            const currentValue = entry.subtypes?.[subtypeDef.subtypeName];
            
            if (subtypeDef.selectionType === 'multi') {
              if (Array.isArray(currentValue) && currentValue.length > 0) {
                hasAtLeastOneSubtypeSelected = true;
              }
            } else {
              // Check for valid non-empty values (including '0' as a valid selection)
              if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                hasAtLeastOneSubtypeSelected = true;
              }
            }
          });
          
          if (!hasAtLeastOneSubtypeSelected) {
            missingSubtypes.push(`${itemLabel} (${entry.number || 'Item'})`);
          }
        }
      }
    });

    if (missingSubtypes.length > 0) {
      return `⚠️ Please select at least one subtype option for: ${missingSubtypes.join(', ')}. Subtype selection is required when subtypes are available.`;
    }

    return null;
  };

  // Update warning message and validation errors when entries or subtypes change
  useEffect(() => {
    // Only show warning if there are entries and product type is set
    const productType = formData.productType || product.type || '';
    if (!productType || (frameEntries.length === 0 && panelEntries.length === 0 && panelWindowEntries.length === 0 && netEntries.length === 0)) {
      setWarning(null);
      // Clear subtype validation errors when no entries
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.includes('subtype-')) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
      return;
    }
    
    const warningMessage = checkMissingSubtypes();
    setWarning(warningMessage);
    
    // Proactively set validation errors for missing subtypes to highlight fields
    if (warningMessage) {
      const productTypeForValidation = formData.productType || product.type || '';
      const allEntries = [
        ...frameEntries.map((entry, index) => ({ entry, type: 'frame', index, itemType: 'Frame' })),
        ...panelEntries.map((entry, index) => ({ entry, type: 'panel', index, itemType: 'Panel' })),
        ...panelWindowEntries.map((entry, index) => ({ entry, type: 'panelWindow', index, itemType: 'Panel Window' })),
        ...netEntries.map((entry, index) => ({ entry, type: 'net', index, itemType: 'Net Fixing' }))
      ];

      const newErrors: {[key: string]: boolean} = {};
      
      allEntries.forEach(({ entry, type, index, itemType }) => {
        // Only check if subtypes exist for this item type and it's a valid array with items
        if (availableSubtypes[itemType] && Array.isArray(availableSubtypes[itemType]) && availableSubtypes[itemType].length > 0) {
          const subtypesForItem = availableSubtypes[itemType];
          const validSubtypes = subtypesForItem.filter(subtypeDef => 
            subtypeDef && 
            subtypeDef.subtypeName && 
            subtypeDef.subtypeName.trim() !== '' && 
            subtypeDef.subtypeName !== 'None'
          );
          
          // Only require selection if there are actually valid subtypes available
          if (validSubtypes.length > 0) {
            let hasAtLeastOneSubtypeSelected = false;
            
            validSubtypes.forEach((subtypeDef) => {
              const currentValue = entry.subtypes?.[subtypeDef.subtypeName];
              
              if (subtypeDef.selectionType === 'multi') {
                if (Array.isArray(currentValue) && currentValue.length > 0) {
                  hasAtLeastOneSubtypeSelected = true;
                }
              } else {
                // Check for valid non-empty values (including '0' as a valid selection)
                if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
                  hasAtLeastOneSubtypeSelected = true;
                }
              }
            });
            
            // If no subtype is selected, mark all subtypes as errors
            if (!hasAtLeastOneSubtypeSelected) {
              validSubtypes.forEach((subtypeDef) => {
                newErrors[`${type}-${index}-subtype-${subtypeDef.subtypeName}`] = true;
              });
            }
          }
        }
      });
      
      // Update validation errors, preserving existing non-subtype errors
      setValidationErrors(prev => {
        const cleaned = { ...prev };
        // Remove old subtype errors
        Object.keys(cleaned).forEach(key => {
          if (key.includes('subtype-')) {
            delete cleaned[key];
          }
        });
        // Add new subtype errors
        return { ...cleaned, ...newErrors };
      });
    } else {
      // Clear subtype validation errors when warning is cleared
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.includes('subtype-')) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  }, [frameEntries, panelEntries, panelWindowEntries, netEntries, availableSubtypes, formData.productType, product.type]);

  const handleSubmit = async () => {
        
    // Check if form is read-only
    if (isReadOnly) {
      console.warn('⚠️ Form is READ-ONLY, blocking submission');
      return;
    }
    console.log("🚨 handleSubmit fired at:", new Date().toISOString());
    console.trace("handleSubmit stack");
    if (!validateForm()) {
      // Check if there are subtype validation errors
      const hasSubtypeErrors = Object.keys(validationErrors).some(key => key.includes('subtype-'));
      const errorMessage = hasSubtypeErrors 
        ? '⚠️ Cannot submit: Please select at least one subtype option for all items that have subtypes available. Subtype selection is mandatory when subtypes are configured.'
        : 'Please fill in all required fields with valid values';
      setError(errorMessage);
      return;
    }

    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const productConfiguration: ProductConfiguration = {
        ...formData,
        frameEntries,
        panelEntries,
        panelWindowEntries,
        netEntries
      };
    
    // Transform entries to items for the product
    const items = [
      ...frameEntries.map(entry => ({
        id: entry.number,
        number: entry.number,
        item_tracking_code: `ITM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        order_tracking_code: product.order_tracking_code || '',
        product_tracking_code: product.product_tracking_code || '',
        customer_tracking_code: product.customer_tracking_code || '',
        type: 'Frame' as const,
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        status: 'pending' as const,
        current_stage: 'Created',
        current_stage_order: 0,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        supervisor_id: null,
        started_at: null,
        completed_at: null,
        estimated_hours: null,
        actual_hours: null,
        workflow_data: {},
        metadata: {},
        subtype: cleanSubtypeObject(entry.subtypes), // Clean and include subtypes in JSONB format
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date(),
        updatedAt: new Date(),
        productId: product.id,
        deleted: false,
        deleted_at: null,
        deleted_by: null,
        logs: []
      })),
      ...panelEntries.map(entry => ({
        id: entry.number,
        number: entry.number,
        item_tracking_code: `ITM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        order_tracking_code: product.order_tracking_code || '',
        product_tracking_code: product.product_tracking_code || '',
        customer_tracking_code: product.customer_tracking_code || '',
        type: 'Panel' as const,
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        status: 'pending' as const,
        current_stage: 'Created',
        current_stage_order: 0,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        bar_required: entry.bar ?? false,
        member_required: entry.member ?? false,
        glass_required: entry.glass ?? false,
        is_laser_cutting: formData.panelLaserCutting ?? false, // Apply group-level setting to all panels
        assigned_to: null,
        supervisor_id: null,
        started_at: null,
        completed_at: null,
        estimated_hours: null,
        actual_hours: null,
        workflow_data: {},
        metadata: {},
        subtype: cleanSubtypeObject(entry.subtypes), // Clean and include subtypes in JSONB format
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date(),
        updatedAt: new Date(),
        productId: product.id,
        deleted: false,
        deleted_at: null,
        deleted_by: null,
        logs: [],
      })),
      ...panelWindowEntries.map(entry => ({
        id: entry.number,
        number: entry.number,
        item_tracking_code: `ITM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        order_tracking_code: product.order_tracking_code || '',
        product_tracking_code: product.product_tracking_code || '',
        customer_tracking_code: product.customer_tracking_code || '',
        type: 'Panel Window' as const,
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        status: 'pending' as const,
        current_stage: 'Created',
        current_stage_order: 0,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        supervisor_id: null,
        started_at: null,
        completed_at: null,
        estimated_hours: null,
        actual_hours: null,
        workflow_data: {},
        metadata: {},
        subtype: cleanSubtypeObject(entry.subtypes), // Clean and include subtypes in JSONB format
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date(),
        updatedAt: new Date(),
        productId: product.id,
        deleted: false,
        deleted_at: null,
        deleted_by: null,
        logs: []
      })),
      ...netEntries.map(entry => ({
        id: entry.number,
        number: entry.number,
        item_tracking_code: `ITM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        order_tracking_code: product.order_tracking_code || '',
        product_tracking_code: product.product_tracking_code || '',
        customer_tracking_code: product.customer_tracking_code || '',
        type: 'Net Fixing' as const,
        width: parseInt(entry.width) || 0,
        height: parseInt(entry.height) || 0,
        quantity: 1,
        status: 'pending' as const,
        current_stage: 'Created',
        current_stage_order: 0,
        material: 'Aluminum',
        color: formData.color || 'Silver',
        finish: 'Standard',
        assigned_to: null,
        supervisor_id: null,
        started_at: null,
        completed_at: null,
        estimated_hours: null,
        actual_hours: null,
        workflow_data: {},
        metadata: {},
        subtype: cleanSubtypeObject(entry.subtypes), // Clean and include subtypes in JSONB format
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date(),
        updatedAt: new Date(),
        productId: product.id,
        deleted: false,
        deleted_at: null,
        deleted_by: null,
        logs: []
      }))
    ];
    
    // Calculate completed items count - preserve existing completed items if editing
    const completedItemsCount = isEditMode ? 
      (product.completed_items || 0) : 
      0; // For new products, all items start as 'pending' so completed count is 0

    const updatedProduct: Product = {
      ...product,
      name: formData.label || product.name, // Use label as name if available, fallback to existing name
      label: formData.label,
      location: formData.location,
      type: formData.productType as "window" | "door" | "ventilator" | "net fix",
      color: formData.color || product.color,
      total_items: items.length,
      completed_items: completedItemsCount,
      items: items,
      metadata: {
        ...product.metadata,
        configuration: productConfiguration
      }
    };

    console.log('🔄 ItemConfigurationPanel - Updated Product:', {
      id: updatedProduct.id,
      name: updatedProduct.name,
      type: updatedProduct.type,
      label: updatedProduct.label,
      location: updatedProduct.location,
      total_items: updatedProduct.total_items,
      completed_items: updatedProduct.completed_items,
      items_count: items.length,
      frame_count: frameEntries.length,
      panel_count: panelEntries.length,
      panel_window_count: panelWindowEntries.length,
      net_count: netEntries.length
    });

    // Handle API calls directly - add product to backend immediately
    const itemTypesPayload = transformProductToItemTypes(updatedProduct);
    console.log('📦 Product payload itemTypes:', JSON.stringify(itemTypesPayload, null, 2));
    
    // Log subtypes for each item type
    Object.keys(itemTypesPayload).forEach(itemTypeKey => {
      const items = itemTypesPayload[itemTypeKey];
      items.forEach((item: any, index: number) => {
        console.log(`📦 ${itemTypeKey}[${index}] subtypes:`, item.subtype, 'Number:', item.number);
      });
    });
    
    const productPayload = {
      name: updatedProduct.name,
      type: updatedProduct.type,
      evaOrder: updatedProduct.evaOrder,
      label: updatedProduct.label,
      location: updatedProduct.location,
      notes: updatedProduct.notes,
      description: (updatedProduct as any).description,
      color: updatedProduct.color,
      itemTypes: itemTypesPayload
    };
    
    console.log('📦 Full product payload:', JSON.stringify(productPayload, null, 2));

    let response;
    
    if (isEditMode && product.id && orderId) {
      // For edit mode - update existing product in order
      console.log('🔄 ItemConfigurationPanel - Edit mode, updating product in order:', updatedProduct.id);
      response = await orderService.updateProductInOrder(orderId, updatedProduct.id, productPayload);
      
      if (response.success) {
        console.log('✅ Product updated successfully in order');
        // Fetch fresh order data
        const orderResponse = await orderService.getOrder(orderId);
        if (orderResponse.success) {
          // Notify parent with fresh order data
          if (onOrderUpdated) {
            onOrderUpdated(orderResponse.data);
          }
        }
        
        // Call completion callbacks to close panel and refresh UI
        if (onUpdate) {
          onUpdate(updatedProduct);
        }
        onComplete(updatedProduct);
      } else {
        throw new Error(response.error?.message || 'Failed to update product in order');
      }
    } else if (orderId) {
      // For new products in edit mode - add product to existing order
      console.log('🔄 ItemConfigurationPanel - Adding new product to existing order:', orderId);
      response = await orderService.addProductToOrder(orderId, productPayload);
      
      if (response.success) {
        console.log('✅ Product added successfully to existing order');
        // Fetch fresh order data
        const orderResponse = await orderService.getOrder(orderId);
        if (orderResponse.success) {
          // Notify parent with fresh order data
          if (onOrderUpdated) {
            onOrderUpdated(orderResponse.data);
          }
        }
        
        // Call completion callbacks to close panel and refresh UI
        onComplete(updatedProduct);
      } else {
        throw new Error(response.error?.message || 'Failed to add product to order');
      }
    } else if (customerId && siteId && onOrderCreated) {
      // For create mode - create order with first product
      console.log('🔄 ItemConfigurationPanel - Creating order with first product');
      const orderPayload = {
        customerId: customerId,
        siteId: siteId,
        amount: '0', // Will be updated later
        paymentStatus: 'pending',
        priority: 'medium',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        notes: '',
        orderLabel: `Order-${Date.now()}`,
        orderNotes: '',
        firstProduct: productPayload
      };
      
      response = await orderService.createOrderWithFirstProduct(orderPayload);
      
      if (response.success) {
        console.log('✅ Order created successfully with first product');
        // Notify parent with created order
        onOrderCreated(response.data.order);
        
        // Call completion callback to close panel
        onComplete(updatedProduct);
      } else {
        throw new Error(response.error?.message || 'Failed to create order with first product');
      }
    } else {
      // Fallback to callback-based approach for other cases
      console.log('🔄 ItemConfigurationPanel - Using callback approach');
      if (isEditMode && onUpdate) {
        onUpdate(updatedProduct);
      } else if (isIncrementalMode && onIncrementalComplete) {
        onIncrementalComplete(updatedProduct);
      } else {
        onComplete(updatedProduct);
      }
      return;
    }
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} product to order:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'add'} product to order. Please try again.`);
    } finally {
      setLoading(false);
    }
  };



  const renderEntryRow = (entry: any, index: number, type: string) => {
    const isPanel = type === 'panel';
    const showCheckboxes = isPanel;
    const isNet = type === 'net'
    const showRadiobtn = isNet
    const isFrame = type === 'frame';
    const radiobtnFrame = isFrame
    const widthError = validationErrors[`${type}-${index}-width`];
    const heightError = validationErrors[`${type}-${index}-height`];
    
    // Map item type to display name for subtype lookup
    const itemTypeMap: Record<string, string> = {
      'frame': 'Frame',
      'panel': 'Panel',
      'panelWindow': 'Panel Window',
      'net': 'Net Fixing'
    };
    const itemTypeForSubtypes = itemTypeMap[type] || type;
    
    // Get subtypes for this item type - fully dynamic based on master data
    // The API already filters by product_type and item_type, so we just use what's returned
    const subtypesForThisItem = availableSubtypes[itemTypeForSubtypes] || [];
    console.log('🔎 Subtypes lookup:', { 
      type, 
      itemTypeForSubtypes, 
      availableSubtypesKeys: Object.keys(availableSubtypes),
      subtypesForThisItem,
      subtypesCount: subtypesForThisItem.length,
      availableSubtypes
    });
    
    // Use original item data if available, otherwise fall back to entry data
    const originalItem = entry.originalItem;
    const displayNumber = originalItem ?
      ((originalItem as any)?.number || (originalItem as any)?.itemNumber || entry.number) :
      entry.number;

      const labelStyle = {
        fontSize: "0.6rem",
        margin: 0,
        "& .MuiFormControlLabel-label": {
          fontSize: "0.6rem",
          paddingLeft: "2px",
        },
      };

  
    
    return (
      <Box key={entry.number} sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr 80px 80px auto auto',
          sm: '120px 80px 80px auto auto',
          md: '140px 90px 90px auto auto'
        },
        gap: { xs: 0.5, sm: 1, md: 1.5 }, 
        alignItems: 'center', 
        mb: 1,
        p: { xs: 0.5, sm: 1, md: 1 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'transparent',
        position: 'relative',
      }}>
       
       
        <TextField
          label="Item Number"
          value={typeof displayNumber === 'string' ? displayNumber : String(displayNumber || '')}
          onChange={(e) => handleEntryChange(type, index, 'number', e.target.value)}
          size="small"
          disabled={true}
          InputProps={{
            style: {
              backgroundColor: '#e3f2fd',
              fontWeight: 'bold',
              color: '#1976d2',
              fontSize: '16px',
              fontFamily: 'monospace'
            }
          }}
          sx={{
            minWidth: { xs: '80px', sm: '100px' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#1976d2',
                borderWidth: 2
              }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '12px', sm: '14px' },
              fontWeight: 'bold'
            }
          }}
        />
        <TextField
          label="Width"
          value={entry.width}
          onChange={(e) => handleEntryChange(type, index, 'width', e.target.value)}
          size="small"
          type="text"
          inputProps={{ min: 1, step: 1 }}
          disabled={isReadOnly}
          error={widthError}
          InputProps={{
            style: {
              fontSize: '16px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              backgroundColor: widthError ? '#ffebee' : 'transparent',
            }
          }}
          sx={{
            minWidth: { xs: '60px', sm: '70px' },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '12px', sm: '14px' },
              fontWeight: 'bold'
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: widthError ? '#f44336' : undefined,
                borderWidth: widthError ? 2 : 1
              }
            },
            padding: 0,
          }}
        />
        <TextField
          label="Height"
          value={entry.height}
          onChange={(e) => handleEntryChange(type, index, 'height', e.target.value)}
          size="small"
          type="text"
          inputProps={{ min: 1, step: 1 }}
          disabled={isReadOnly}
          error={heightError}
          InputProps={{
            style: {
              fontSize: '16px',
              fontFamily: 'monospace',
              fontWeight: 'bold',
              backgroundColor: heightError ? '#ffebee' : 'transparent'
            }
          }}
          sx={{
            minWidth: { xs: '60px', sm: '70px' },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '12px', sm: '14px' },
              fontWeight: 'bold'
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: heightError ? '#f44336' : undefined,
                borderWidth: heightError ? 2 : 1
              }
            }
          }}
        />
        {showCheckboxes && (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 0.5, sm: 1 },
            minWidth: 'fit-content',
            flexWrap: 'nowrap',
            alignItems: 'center'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={entry.member || false}
                  onChange={(e) => handleEntryChange(type, index, 'member', e.target.checked)}
                  disabled={isReadOnly}
                  size="small"
                />
              }
              label="Member"
              sx={{ 
                fontSize: '0.6rem',
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.6rem',
                  paddingLeft: '2px'
                }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={entry.bar || false}
                  onChange={(e) => handleEntryChange(type, index, 'bar', e.target.checked)}
                  disabled={isReadOnly}
                  size="small"
                />
              }
              label="Bar"
              sx={{ 
                fontSize: '0.6rem',
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.6rem',
                  paddingLeft: '2px'
                }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={entry.glass || false}
                  onChange={(e) => handleEntryChange(type, index, 'glass', e.target.checked)}
                  disabled={isReadOnly}
                  size="small"
                />
              }
              label="Glass"
              sx={{ 
                fontSize: '0.6rem',
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.6rem',
                  paddingLeft: '2px'
                }
              }}
            />
          </Box>
        )}

        {/* Dynamic Subtype Fields - In same row */}
        {(() => {
          // Debug: Log subtype availability
          console.log('🎯 Rendering subtypes for entry:', {
            type,
            index,
            itemTypeForSubtypes,
            subtypesForThisItem,
            subtypesCount: subtypesForThisItem?.length || 0,
            availableSubtypesKeys: Object.keys(availableSubtypes),
            availableSubtypes
          });
          
          if (!subtypesForThisItem || subtypesForThisItem.length === 0) {
            return null;
          }
          
          // Filter and check if we should hide the section
          // Filter out "None" subtypes if there are other valid subtypes
          const allValidSubtypes = subtypesForThisItem
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
            .filter(subtypeDef => subtypeDef.subtypeName && subtypeDef.subtypeName.trim() !== '');
          
          // Separate "None" subtypes from real subtypes
          const noneSubtypes = allValidSubtypes.filter(s => s.subtypeName === 'None');
          const realSubtypes = allValidSubtypes.filter(s => s.subtypeName !== 'None');
          
          // If there are real subtypes, exclude "None" subtypes
          // Only show "None" if it's the only subtype and it's required
          const validSubtypes = realSubtypes.length > 0 
            ? realSubtypes  // Show only real subtypes, exclude "None"
            : (noneSubtypes.length > 0 && noneSubtypes[0].isRequired 
                ? noneSubtypes  // Show "None" only if it's required and there are no other subtypes
                : []);  // Hide if only non-required "None" exists
          
          // Hide section if no valid subtypes
          if (validSubtypes.length === 0) {
            return null; // Hide the subtype section
          }
          
          return (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              minWidth: 'fit-content',
              alignItems: 'center'
            }}>
              {validSubtypes.map((subtypeDef) => {
                const currentValue = entry.subtypes?.[subtypeDef.subtypeName];
                console.log('🔍 Reading subtype value:', { subtypeName: subtypeDef.subtypeName, currentValue, valueType: typeof currentValue, isArray: Array.isArray(currentValue), entrySubtypes: entry.subtypes });
                
                // Use UI component type from master configuration
                // If not specified, fall back to: dropdown for single-select, checkboxes for multi-select
                const subtypeDefAny = subtypeDef as any;
                const rawUiComponentType = subtypeDefAny.uiComponentType || subtypeDefAny.ui_component_type;
                // Normalize to lowercase for case-insensitive comparison
                const uiComponentType = rawUiComponentType 
                  ? String(rawUiComponentType).toLowerCase() 
                  : (subtypeDef.selectionType === 'single' ? 'dropdown' : 'checkbox');
                const useDropdown = uiComponentType === 'dropdown';
                const useCheckbox = uiComponentType === 'checkbox';
                const useRadio = uiComponentType === 'radio';
                
                console.log('🎨 UI Component Type:', { 
                  subtypeName: subtypeDef.subtypeName, 
                  uiComponentType, 
                  useDropdown, 
                  useCheckbox, 
                  useRadio,
                  rawValue: subtypeDefAny.uiComponentType,
                  rawValue2: subtypeDefAny.ui_component_type,
                  selectionType: subtypeDef.selectionType
                });
                
                // IMPORTANT: Only ONE component should render per subtype
                // Checkbox: Show for checkbox component type (regardless of selection type)
                const subtypeError = validationErrors[`${type}-${index}-subtype-${subtypeDef.subtypeName}`];
                
                if (useCheckbox) {
                  // Show checkboxes inline - RETURN EARLY to prevent other components from rendering
                  // Don't show label for checkboxes - the checkbox labels are self-explanatory
                  return (
                    <Box key={subtypeDef.subtypeName} sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      gap: 0.5, 
                      alignItems: 'center', 
                      flexWrap: 'nowrap', 
                      whiteSpace: 'nowrap',
                      border: subtypeError ? '2px solid' : 'none',
                      borderColor: subtypeError ? 'error.main' : 'transparent',
                      borderRadius: subtypeError ? '4px' : 0,
                      padding: subtypeError ? '6px' : 0,
                      backgroundColor: subtypeError ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
                      boxShadow: subtypeError ? '0 0 0 2px rgba(211, 47, 47, 0.2)' : 'none',
                      transition: 'all 0.3s ease'
                    }}>
                      {/* Don't show "None" option for checkboxes - users can simply uncheck all options */}
                      {subtypeDef.options
                        .filter(option => option.value !== 'None' && option.value !== '') // Exclude "None" and empty values from checkbox options
                        .sort((a, b) => (a.optionOrder || 0) - (b.optionOrder || 0))
                        .map((option) => {
                          const isMultiSelect = subtypeDef.selectionType === 'multi';
                          // For single-select, treat as string; for multi-select, treat as array
                          const selectedValues = isMultiSelect 
                            ? (Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []))
                            : (currentValue ? [currentValue] : []);
                          const isChecked = isMultiSelect
                            ? selectedValues.includes(option.value)
                            : (currentValue === option.value);
                          
                          return (
                            <FormControlLabel
                              key={option.value}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (isMultiSelect) {
                                      // Multi-select: allow multiple selections
                                      const current = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
                                      const newValue = e.target.checked
                                        ? [...current, option.value]
                                        : current.filter((v: string) => v !== option.value);
                                      console.log('☑️ Subtype Checkbox (Multi) onChange:', { subtypeName: subtypeDef.subtypeName, optionValue: option.value, checked: e.target.checked, newValue });
                                      handleEntryChange(type, index, `subtype_${subtypeDef.subtypeName}`, newValue);
                                    } else {
                                      // Single-select: only allow one selection (like radio buttons)
                                      const newValue = e.target.checked ? option.value : '';
                                      console.log('☑️ Subtype Checkbox (Single) onChange:', { subtypeName: subtypeDef.subtypeName, optionValue: option.value, checked: e.target.checked, newValue });
                                      handleEntryChange(type, index, `subtype_${subtypeDef.subtypeName}`, newValue);
                                    }
                                  }}
                                  disabled={isReadOnly}
                                  size="small"
                                />
                              }
                              label={option.label || option.value}
                              sx={{ 
                                fontSize: '0.6rem',
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.6rem',
                                  paddingLeft: '2px'
                                }
                              }}
                            />
                          );
                        })}
                    </Box>
                  );
                }
                
                // Radio: Show for radio component type (regardless of selection type)
                // RETURN EARLY to prevent dropdown from rendering
                if (useRadio) {
                  // Radio buttons: Show for single-select
                  // Don't show label for radio buttons - the radio button labels are self-explanatory
                  return (
                    <Box key={subtypeDef.subtypeName} sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      gap: 0.5, 
                      alignItems: 'center', 
                      flexWrap: 'nowrap', 
                      whiteSpace: 'nowrap',
                      border: subtypeError ? '2px solid' : 'none',
                      borderColor: subtypeError ? 'error.main' : 'transparent',
                      borderRadius: subtypeError ? '4px' : 0,
                      padding: subtypeError ? '6px' : 0,
                      backgroundColor: subtypeError ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
                      boxShadow: subtypeError ? '0 0 0 2px rgba(211, 47, 47, 0.2)' : 'none',
                      transition: 'all 0.3s ease'
                    }}>
                      <RadioGroup
                        row
                        value={currentValue || ''}
                        onChange={(e) => {
                          console.log('🔘 Subtype Radio onChange:', { subtypeName: subtypeDef.subtypeName, selectedValue: e.target.value });
                          handleEntryChange(type, index, `subtype_${subtypeDef.subtypeName}`, e.target.value);
                        }}
                        disabled={isReadOnly}
                        sx={{ gap: 0.5, display: 'flex', flexWrap: 'nowrap' }}
                      >
                        {/* Add "None" option for radio buttons if not required and not already present */}
                        {!subtypeDef.isRequired && subtypeDef.subtypeName !== 'None' && !subtypeDef.options.some(opt => opt.value === 'None' || opt.value === '') && (
                          <FormControlLabel
                            control={<Radio disabled={isReadOnly} size="small" />}
                            label="None"
                            value=""
                            sx={{ 
                              fontSize: '0.6rem',
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '0.6rem',
                                paddingLeft: '2px'
                              }
                            }}
                          />
                        )}
                        {subtypeDef.options
                          .filter(option => option.value !== 'None' && option.value !== '') // Exclude "None" and empty values from radio options
                          .sort((a, b) => (a.optionOrder || 0) - (b.optionOrder || 0))
                          .map((option) => (
                            <FormControlLabel
                              key={option.value}
                              control={<Radio disabled={isReadOnly} size="small" />}
                              label={option.label || option.value}
                              value={option.value}
                              sx={{ 
                                fontSize: '0.6rem',
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.6rem',
                                  paddingLeft: '2px'
                                }
                              }}
                            />
                          ))}
                      </RadioGroup>
                    </Box>
                  );
                } 
                
                // Dropdown: Default fallback for dropdown component type or if no specific type is set
                // For multi-select in dropdown, show selected values as comma-separated
                const isMultiSelectInDropdown = subtypeDef.selectionType === 'multi' && useDropdown;
                
                return (
                    <Box key={subtypeDef.subtypeName} sx={{ 
                      minWidth: '120px', 
                      maxWidth: '200px',
                      border: subtypeError ? '2px solid' : 'none',
                      borderColor: subtypeError ? 'error.main' : 'transparent',
                      borderRadius: subtypeError ? '4px' : 0,
                      padding: subtypeError ? '2px' : 0,
                      backgroundColor: subtypeError ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
                      boxShadow: subtypeError ? '0 0 0 2px rgba(211, 47, 47, 0.2)' : 'none',
                      transition: 'all 0.3s ease'
                    }}>
                      <FormControl size="small" fullWidth error={!!subtypeError}>
                        <Select
                          value={isMultiSelectInDropdown ? '' : (currentValue || '')}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            console.log('📋 Subtype Select onChange:', { subtypeName: subtypeDef.subtypeName, selectedValue, valueType: typeof selectedValue, isMultiSelect: isMultiSelectInDropdown });

                            if (isMultiSelectInDropdown) {
                              // For multi-select in dropdown, toggle the selected value
                              const current = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
                              const newValue = current.includes(selectedValue)
                                ? current.filter((v: string) => v !== selectedValue)
                                : [...current, selectedValue];
                              handleEntryChange(type, index, `subtype_${subtypeDef.subtypeName}`, newValue);
                            } else {
                              // Single-select: just set the value
                              handleEntryChange(type, index, `subtype_${subtypeDef.subtypeName}`, selectedValue);
                            }
                          }}
                          disabled={isReadOnly}
                          displayEmpty
                          sx={{
                            fontSize: '16px',
                            fontFamily: 'monospace',
                            fontWeight: 'bold',
                            height: '40px',
                            '& .MuiSelect-select': {
                              fontSize: '16px',
                              fontFamily: 'monospace',
                              fontWeight: 'bold'
                            },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderWidth: subtypeError ? 2 : 1,
                                borderColor: subtypeError ? 'error.main' : undefined
                              }
                            },
                            padding: 0,
                          }}
                          renderValue={(selected) => {
                            if (isMultiSelectInDropdown) {
                              // For multi-select, show selected values
                              if (Array.isArray(currentValue) && currentValue.length > 0) {
                                const labels = currentValue.map(val => {
                                  const option = subtypeDef.options.find(opt => opt.value === val);
                                  return option?.label || option?.value || val;
                                });
                                return labels.join(', ');
                              }
                              return <p style={{ fontSize: '16px', color: '#999', fontFamily: 'monospace', fontWeight: 'bold' }}>{subtypeDef.subtypeName}</p>;
                            }

                            // Single-select display
                            if (!selected) {
                              return <p style={{ fontSize: '14px', color: '#999', fontFamily: 'monospace', fontWeight: 'bold' }}>{subtypeDef.subtypeName}</p>;
                            }
                            const option = subtypeDef.options.find(opt => opt.value === selected);
                            return option?.label || option?.value || selected;
                          }}
                        >
                          <MenuItem value="" disabled={subtypeDef.isRequired} sx={{ fontSize: '0.875rem' }}>
                            <p>{subtypeDef.isRequired ? `Select ${subtypeDef.subtypeName}` : `Select ${subtypeDef.subtypeName}`}</p>
                          </MenuItem>
                          {subtypeDef.options
                            .filter(option => option.value !== 'None' && option.value !== '') // Exclude "None" and empty values from dropdown options
                            .sort((a, b) => (a.optionOrder || 0) - (b.optionOrder || 0))
                            .map((option) => {
                              const isSelected = isMultiSelectInDropdown 
                                ? (Array.isArray(currentValue) && currentValue.includes(option.value))
                                : (currentValue === option.value);
                              
                              return (
                                <MenuItem 
                                  key={option.value} 
                                  value={option.value} 
                                  sx={{ 
                                    fontSize: '0.875rem',
                                    backgroundColor: isSelected && isMultiSelectInDropdown ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                                    fontWeight: isSelected && isMultiSelectInDropdown ? 'bold' : 'normal'
                                  }}
                                >
                                  {isMultiSelectInDropdown && (
                                    <Checkbox 
                                      checked={isSelected} 
                                      size="small" 
                                      sx={{ padding: '2px', marginRight: '4px' }}
                                    />
                                  )}
                                  {option.label || option.value}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    </Box>
                  );
              })}
            </Box>
          );
        })()}

    

        {/* {radiobtnFrame && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
              flexWrap: "nowrap",
            }}
          >
            {product.type === "door" && (
              <>
            
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={entry.meshType === "patti"}
                      onChange={() =>
                        entry.meshType !== "patti" &&
                        handleEntryChange(type, index, "meshType", "patti")
                      }
                      size="small"
                      sx={{ padding: "2px" }}
                    />
                  }
                  label="Patti"
                  sx={labelStyle}
                />

                 //BOTTOM 
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={entry.meshType === "bottom"}
                      onChange={() =>
                        entry.meshType !== "bottom" &&
                        handleEntryChange(type, index, "meshType", "bottom")
                      }
                      size="small"
                      sx={{ padding: "2px" }}
                    />
                  }
                  label="Bottom"
                  sx={labelStyle}
                />

                // SECTION  
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={entry.meshType === "section"}
                      onChange={() =>
                        entry.meshType !== "section" &&
                        handleEntryChange(type, index, "meshType", "section")
                      }
                      size="small"
                      sx={{ padding: "2px" }}
                    />
                  }
                  label="Section"
                  sx={labelStyle}
                />
              </>
            )}
          </Box>
        )} */}

      </Box>
    );
  };

  const { options,  } = useMasterDropdown({
      masterType: "sets",
      enabled: true,
    });

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      <div
        className={`absolute right-0 top-0 h-full w-full sm:w-[80%] md:w-[60%] lg:w-[50%] xl:w-[40%] 2xl:w-[35%] min-w-[320px] max-w-[800px] bg-white shadow-xl transform transition-transform duration-500 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
      
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {formData.label && formData.location 
                    ? `${formData.label} - ${formData.location}` 
                    : `${isEditMode ? 'Edit' : 'Configure'} Items - ${product?.name || 'Set'}`}
                </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 flex-shrink-0"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}




            {/* Sequential Fields - Responsive */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>

              <Box sx={{ flex: 1 }}>
                <MasterDropdown
                  options={options}
                  value={formData.productType}
                  onChange={(value) => handleInputChange('productType', value)}
                  loading={false}
                  label="Set Type"
                  fullWidth
                  size="small"
                  disabled={isEditMode || isReadOnly}
                />
              </Box> 

              <TextField
                label="Label"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
                autoFocus
                disabled={isReadOnly}
              />
              
              <TextField
                select
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
                disabled={locationLoading || isReadOnly}
              >
                <MenuItem value="">Select Location</MenuItem>
                {locationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>           
              
              <TextField
                select
                label="Color"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                sx={{ flex: 1 }}
                size="small"
                disabled={colorLoading || isReadOnly}
              >
                <MenuItem value="">Select Color</MenuItem>
                {colorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

            </Box>

            {/* Counts and Checkboxes - Responsive */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1.5, sm: 2 }, 
              mb: 2 
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.frameChecked}
                    onChange={(e) => handleInputChange('frameChecked', e.target.checked)}
                    disabled={isReadOnly}
                    // Frame checkbox is always enabled - frame is always available
                  />
                }
                label="Frame"
                sx={{ opacity: isReadOnly ? 0.6 : 1 }}
              />
              
              <TextField
                select
                label="Panel"
                value={formData.panelCount}
                onChange={(e) => handleInputChange('panelCount', parseInt(e.target.value))}
                size="small"
                sx={{ minWidth: 120 }}
                disabled={formData.productType?.toLowerCase() === 'ventilator' || isReadOnly}
              >
                {PANEL_COUNT_OPTIONS.map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Panel Window"
                value={formData.panelWindowCount}
                onChange={(e) => handleInputChange('panelWindowCount', parseInt(e.target.value))}
                size="small"
                sx={{ minWidth: 120 }}
                disabled={formData.productType?.toLowerCase() === 'window' || formData.productType?.toLowerCase() === 'ventilator' || isReadOnly}
              >
                {PANEL_COUNT_OPTIONS.map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Net"
                value={formData.netCount}
                onChange={(e) => handleInputChange('netCount', parseInt(e.target.value))}
                size="small"
                sx={{ minWidth: 120 }}
                disabled={isReadOnly}
              >
                {/* {Array.from({ length: formData.panelCount + 1}, (_, i) => i ).map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))} */}
                {PANEL_COUNT_OPTIONS.map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Auto-Sync Toggle */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              p: 1,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              opacity: isReadOnly ? 0.6 : 1
            }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoSyncDimensions}
                    onChange={(e) => setAutoSyncDimensions(e.target.checked)}
                    color="primary"
                    disabled={isReadOnly}
                  />
                }
                label="Auto-sync dimensions"
              />
              <Typography variant="body2" color="text.secondary">
                When enabled, entering width/height and toggling member/glass/bar in the first panel will automatically apply to all other panels
              </Typography>
            </Box>

            {/* Frame Entries */}
            {formData.frameChecked && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2}}>
                  <Typography variant="h6">Frame</Typography>
                  {/* <Box sx={{ 
                    px: 1, 
                    py: 0.2, 
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    border: '1px solid #e0e0e0',
                    bgcolor: '#f5f5f5'
                  }}>
                    {frameEntries.length} Part{frameEntries.length !== 1 ? 's' : ''}
                  </Box> */}
                  {/* <Chip
                    label={{frameEntries.length} Part{frameEntries.length !== 1 ? 's' : ''}}
                    size="small"
                    color='#f5f5f5'
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  /> */}
                  <Chip
                    label={`${frameEntries.length} Part${frameEntries.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      height: 20,
                      bgcolor: '#f5f5f5ff',       // custom background color
                      border: '1px solid #e0e0e0',   // optional border if you want
                      color: 'black',  
                      borderStyle: 'solid',
                    }}
                  />
                </Box>
                {frameEntries.map((entry, index) => 
                  renderEntryRow(entry, index, 'frame')
                )}
              </Box>
            )}

            {/* Panel Entries */}
            { panelEntries.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">Panel</Typography>
                    {/* <Box sx={{ 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      border: '1px solid #e0e0e0',
                      bgcolor: '#f5f5f5'
                    }}>
                      {panelEntries.length} Part{panelEntries.length !== 1 ? 's' : ''}
                    </Box> */}
                    <Chip
                      label={`${panelEntries.length} Part${panelEntries.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        bgcolor: '#f5f5f5ff',       // custom background color
                        border: '1px solid #e0e0e0',   // optional border if you want
                        color: 'black',  
                        borderStyle: 'solid',
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.panelLaserCutting || false}
                          onChange={(e) => handleInputChange('panelLaserCutting', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Laser Cutting"
                      sx={{ 
                        fontSize: '0.875rem',
                        margin: 0,
                        marginLeft: 1,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                </Box>
                {panelEntries.map((entry, index) => 
                  renderEntryRow(entry, index, 'panel')
                )}
              </Box>
            )}

            {/* Panel Window Entries */}
            {panelWindowEntries.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">Panel Window</Typography>
                    {/* <Box sx={{ 
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      border: '1px solid #e0e0e0',
                      bgcolor: '#f5f5f5'
                    }}>
                      {/* {panelWindowEntries.length} item{panelWindowEntries.length !== 1 ? 's' : ''} */}
                      {/* {panelWindowEntries.length} Part{panelWindowEntries.length !== 1 ? 's' : ''}
                    </Box> */} 
                    <Chip
                      label={`${panelWindowEntries.length} Part ${panelWindowEntries.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        bgcolor: '#f5f5f5ff',       // custom background color
                        border: '1px solid #e0e0e0',   // optional border if you want
                        color: 'black',  
                        borderStyle: 'solid',
                      }}
                    />
                </Box>
                {panelWindowEntries.map((entry, index) => 
                  renderEntryRow(entry, index, 'panelWindow')
                )}
              </Box>
            )}

            {/* Net Entries */}
            {netEntries.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">Net</Typography>
                  {/* <Box sx={{ 
                    px: 2, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    border: '1px solid #e0e0e0',
                    bgcolor: '#f5f5f5'
                  }}>
                    {netEntries.length} Part{netEntries.length !== 1 ? 's' : ''}
                  </Box> */}
                  <Chip
                      label={`${netEntries.length} Part${netEntries.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        bgcolor: '#f5f5f5ff',       // custom background color
                        border: '1px solid #e0e0e0',   // optional border if you want
                        color: 'black',  
                        borderStyle: 'solid',
                      }}
                    />
                </Box>
                {netEntries.map((entry, index) => 
                  renderEntryRow(entry, index, 'net')
                )}
              </Box>
            )}
          </div>

          <div className="p-3 border-t border-gray-200">
            {/* Warning Display for Missing Subtypes - Near Submit Button */}
            {warning && !error && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 2,
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  '& .MuiAlert-icon': {
                    color: '#856404'
                  }
                }}
              >
                {warning}
              </Alert>
            )}
            
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={loading || !isFormValid() || isReadOnly}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              sx={{
                backgroundColor: (!isFormValid() || loading || isReadOnly) ? '#ccc' : undefined,
                '&:hover': {
                  backgroundColor: (!isFormValid() || loading || isReadOnly) ? '#ccc' : undefined
                }
              }}
              title={isReadOnly ? "Cannot edit - order is in progress or completed" : ""}
            >
              {loading ? 'Processing...' : (isEditMode ? 'UPDATE' : 'SUBMIT')}
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ItemConfigurationPanel;
